import { from, of, throwError, Observable, scheduled } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";
import { Injectable, Inject, Optional, InjectionToken } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { ClientBase } from "./client-base";
import { ConfigService } from "src/app/config/config.service";

import * as moment from "moment";
import { RequestDto } from "./dashboard.client";

export const API_BASE_URL = new InjectionToken<string>("API_BASE_URL");
export const API_USER_BASE_URL = "/api/User";

@Injectable()
export class AdditionalContentClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  get(type: AdditionalContentType): Observable<AdditionalContentFixtureDto[]> {
    let url_ = this.baseUrl + "/api/AdditionalContent?";
    if (type === undefined || type === null)
      throw new Error(
        "The parameter 'type' must be defined and cannot be null.",
      );
    else url_ += "type=" + encodeURIComponent("" + type) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<AdditionalContentFixtureDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else {
          return <Observable<AdditionalContentFixtureDto[]>>(
            (<any>throwError(() => response_))
          );
        }
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<AdditionalContentFixtureDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(AdditionalContentFixtureDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<AdditionalContentFixtureDto[]>([]);
  }

  create(
    model: CreateAdditionalContentFixtureDto | null | undefined,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    let url_ = this.baseUrl + "/api/AdditionalContent";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<AdditionalContentFixtureDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else {
          return <Observable<AdditionalContentFixtureDetailsDto>>(
            (<any>throwError(() => response_))
          );
        }
      }),
    );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdditionalContentFixtureDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<AdditionalContentFixtureDetailsDto>(<any>null);
  }

  update(
    model: UpdateAdditionalContentFixtureDto | null | undefined,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    let url_ = this.baseUrl + "/api/AdditionalContent";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<AdditionalContentFixtureDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<AdditionalContentFixtureDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdditionalContentFixtureDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<AdditionalContentFixtureDetailsDto>(<any>null);
  }

  getAll(
    clubId: number | null,
    past: boolean,
  ): Observable<{
    [key in keyof typeof AdditionalContentType]?: AdditionalContentFixtureDto[];
  }> {
    let url_ = this.baseUrl + "/api/AdditionalContent/getAll?";
    if (clubId === undefined)
      throw new Error("The parameter 'clubId' must be defined.");
    else if (clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    if (past === undefined || past === null)
      throw new Error(
        "The parameter 'past' must be defined and cannot be null.",
      );
    else url_ += "past=" + encodeURIComponent("" + past) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <
              Observable<{
                [key in keyof typeof AdditionalContentType]?: AdditionalContentFixtureDto[];
              }>
            >(<any>throwError(() => e));
          }
        } else return <
            Observable<{
              [key in keyof typeof AdditionalContentType]?: AdditionalContentFixtureDto[];
            }>
          >(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetAll(response: HttpResponseBase): Observable<{
    [key in keyof typeof AdditionalContentType]?: AdditionalContentFixtureDto[];
  }> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (resultData200) {
            result200 = {} as any;
            for (let key in resultData200) {
              if (resultData200.hasOwnProperty(key))
                (<any>result200)[key] = resultData200[key]
                  ? resultData200[key].map((i: any) =>
                      AdditionalContentFixtureDto.fromJS(i),
                    )
                  : [];
            }
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<{
      [key in keyof typeof AdditionalContentType]?: AdditionalContentFixtureDto[];
    }>(<any>null);
  }

  get2(
    id: string,
    type: AdditionalContentType,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    let url_ = this.baseUrl + "/api/AdditionalContent/{type}/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (type === undefined || type === null)
      throw new Error("The parameter 'type' must be defined.");
    url_ = url_.replace("{type}", encodeURIComponent("" + type));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet2(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet2(<any>response_);
          } catch (e) {
            return <Observable<AdditionalContentFixtureDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<AdditionalContentFixtureDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet2(
    response: HttpResponseBase,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdditionalContentFixtureDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<AdditionalContentFixtureDetailsDto>(<any>null);
  }
}

@Injectable()
export class ApiVersionClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  getHome(): Observable<void> {
    let url_ = this.baseUrl + "/";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    // ...existing code...
    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetHome(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetHome(<any>response_);
          } catch (e) {
            return <Observable<void>>(<any>throwError(() => e));
          }
        } else {
          return <Observable<void>>(<any>throwError(() => response_));
        }
      }),
    );
    // ...existing code...
  }

  protected processGetHome(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return of<void>(<any>null);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<void>(<any>null);
  }

  /**
   * Returns appication api version [No Role].
   * @return Returns application api version
   */
  get(): Observable<string | null> {
    let url_ = this.baseUrl + "/api/apiversion";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<string | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<string | null>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGet(response: HttpResponseBase): Observable<string | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<string | null>(<any>null);
  }

  /**
   * Returns storage shared access signature string [League]
   * @return Storage shared access signature
   */
  getPresentationUrl(): Observable<string> {
    let url_ = this.baseUrl + "/api/ApiVersion/GetPresentationUrl";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetPresentationUrl(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetPresentationUrl(<any>response_);
          } catch (e) {
            return <Observable<string>>(<any>throwError(() => e));
          }
        } else return <Observable<string>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetPresentationUrl(
    response: HttpResponseBase,
  ): Observable<string> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 403) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Returns error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<string>(<any>null);
  }
}

@Injectable()
export class AuthenticationClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Sign method. Returns redirect result from Azure AAD auth. Use this one to auth [No Role].
   */
  signin(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/Authentication/SignIn";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSignin(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSignin(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processSignin(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Signon method. Returns redirect result from Azure AAD auth [No Role].
   */
  signon(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/Authentication/SignOn";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSignon(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSignon(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processSignon(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Sign Out method. Returns redirect result from Azure AAD auth after sign out. Use this one to quit from app [No Role].
   */
  signOut(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/Authentication/SignOut";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSignOut(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSignOut(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processSignOut(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * If AAD sends a single sign-out message to the app, end the user's session, but don't redirect to AAD for sign out [No Role].
   */
  endSession(): Observable<void> {
    let url_ = this.baseUrl + "/Authentication/endsession";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processEndSession(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processEndSession(<any>response_);
          } catch (e) {
            return <Observable<void>>(<any>throwError(() => e));
          }
        } else return <Observable<void>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processEndSession(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return of<void>(<any>null);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<void>(<any>null);
  }

  /**
   * SignedOut method. Returns redirect result from Azure AAD auth [No Role].
   */
  signedOut(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/Authentication/SignedOut";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSignedOut(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSignedOut(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processSignedOut(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }
}

@Injectable()
export class ClubContentSessionParticipantClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of participants for current broadcaster user [Broadcaster].
   * @return Returns list of participants
   */
  getAll(): Observable<ParticipantDto[]> {
    let url_ = this.baseUrl + "/api/ClubContentSessionParticipant";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }

  /**
   * Creates new participant for broadcaster [Broadcaster].
   * @param model (optional) Create participant model
   * @return Returns created participant
   */
  post(
    model: CreateParticipantDto | null | undefined,
  ): Observable<ParticipantDto> {
    let url_ = this.baseUrl + "/api/ClubContentSessionParticipant";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processPost(
    response: HttpResponseBase,
  ): Observable<ParticipantDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ParticipantDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto>(<any>null);
  }

  /**
   * Returns list of participants for current broadcaster user by selected type [Broadcaster].
   * @param type Participant Type
   * @return Returns list of participants
   */
  get2(type: ParticipantType): Observable<ParticipantDto[]> {
    let url_ = this.baseUrl + "/api/ClubContentSessionParticipant/{type}";
    if (type === undefined || type === null)
      throw new Error("The parameter 'type' must be defined.");
    url_ = url_.replace("{type}", encodeURIComponent("" + type));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet2(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet2(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet2(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }

  /**
   * Returns list of participants by broadcaster id (For league) [League].
   * @param companyId Broadcaster company Id. String
   * @return Returns list of participants
   */
  getByCompany(companyId: string | null): Observable<ParticipantDto[]> {
    let url_ =
      this.baseUrl + "/api/ClubContentSessionParticipant/getByCompany?";
    if (companyId === undefined)
      throw new Error("The parameter 'companyId' must be defined.");
    else if (companyId !== null)
      url_ += "companyId=" + encodeURIComponent("" + companyId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetByCompany(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetByCompany(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetByCompany(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }
}

@Injectable()
export class ClubContentSessionRequestClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  get(id: string): Observable<ClubContentSessionRequestDto> {
    let url_ = this.baseUrl + "/api/ClubContentSessionRequest?";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined and cannot be null.");
    else url_ += "id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<ClubContentSessionRequestDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ClubContentSessionRequestDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<ClubContentSessionRequestDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ClubContentSessionRequestDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ClubContentSessionRequestDto>(<any>null);
  }

  create(
    model: CreateAdditionalContentSessionRequestDto | null | undefined,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    let url_ = this.baseUrl + "/api/ClubContentSessionRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<AdditionalContentFixtureDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<AdditionalContentFixtureDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdditionalContentFixtureDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<AdditionalContentFixtureDetailsDto>(<any>null);
  }

  update(
    model: UpdateAdditionalContentSessionRequestDto | null | undefined,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    let url_ = this.baseUrl + "/api/ClubContentSessionRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<AdditionalContentFixtureDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<AdditionalContentFixtureDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<AdditionalContentFixtureDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = AdditionalContentFixtureDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<AdditionalContentFixtureDetailsDto>(<any>null);
  }
}

@Injectable()
export class ClubsClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns List of clubs [No Role].
   * @return Returns List of clubs
   */
  get(): Observable<ClubDto[]> {
    let url_ = this.baseUrl + "/api/Clubs";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<ClubDto[]>>(<any>throwError(() => e));
          }
        } else return <Observable<ClubDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGet(response: HttpResponseBase): Observable<ClubDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ClubDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ClubDto[]>([]);
  }
}

@Injectable()
export class ContentSessionTypeClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of content session types
   */
  getAll(): Observable<ContentSessionTypeDto[]> {
    let url_ = this.baseUrl + "/api/ContentSessionType";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<ContentSessionTypeDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ContentSessionTypeDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<ContentSessionTypeDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ContentSessionTypeDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ContentSessionTypeDto[]>([]);
  }
}

@Injectable()
export class FilmingLocationsClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Get location of all matches [League, Club]
   */
  getAll(): Observable<FilmingLocationInfoDto> {
    let url_ = this.baseUrl + "/api/FilmingLocations/GetAll";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<FilmingLocationInfoDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<FilmingLocationInfoDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<FilmingLocationInfoDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = FilmingLocationInfoDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FilmingLocationInfoDto>(<any>null);
  }

  /**
   * Update a match location [League, Club]
   * @param location (optional)
   */
  update(
    location: MatchFilmingLocationDetailsDto | null | undefined,
  ): Observable<ResultOfMatchFilmingLocationDetailsDto> {
    let url_ = this.baseUrl + "/api/FilmingLocations";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(location);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<ResultOfMatchFilmingLocationDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ResultOfMatchFilmingLocationDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<ResultOfMatchFilmingLocationDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 =
            ResultOfMatchFilmingLocationDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ResultOfMatchFilmingLocationDetailsDto>(<any>null);
  }

  updateForMatches(
    model: MatchFilmingLocationsDto | null | undefined,
  ): Observable<void> {
    let url_ = this.baseUrl + "/api/FilmingLocations/UpdateForMatches";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdateForMatches(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdateForMatches(<any>response_);
          } catch (e) {
            return <Observable<void>>(<any>throwError(() => e));
          }
        } else return <Observable<void>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processUpdateForMatches(
    response: HttpResponseBase,
  ): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<void>(<any>null);
  }
}

@Injectable()
export class FixtureClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of season matches for club [Club].
   * @param nonLiveInUK (optional)
   * @param upcomingMatches (optional)
   * @return Returns list of season match rounds with matches
   */
  getAll(
    nonLiveInUK: boolean | undefined,
    upcomingMatches: boolean | undefined,
  ): Observable<MatchFixtureEventDto[]> {
    let url_ = this.baseUrl + "/api/Fixture?";
    if (nonLiveInUK === null)
      throw new Error("The parameter 'nonLiveInUK' cannot be null.");
    else if (nonLiveInUK !== undefined)
      url_ += "nonLiveInUK=" + encodeURIComponent("" + nonLiveInUK) + "&";
    if (upcomingMatches === null)
      throw new Error("The parameter 'upcomingMatches' cannot be null.");
    else if (upcomingMatches !== undefined)
      url_ +=
        "upcomingMatches=" + encodeURIComponent("" + upcomingMatches) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<MatchFixtureEventDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<MatchFixtureEventDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<MatchFixtureEventDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(MatchFixtureEventDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<MatchFixtureEventDto[]>([]);
  }

  /**
   * Creates fixture event for current club user [Club].
   * @param model (optional) Create Fixture Event Model
   * @return Returns fixture event info
   */
  post(
    model: CreateFixtureEventDto | null | undefined,
  ): Observable<FixtureEventDto> {
    let url_ = this.baseUrl + "/api/Fixture";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<FixtureEventDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FixtureEventDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPost(
    response: HttpResponseBase,
  ): Observable<FixtureEventDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = FixtureEventDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FixtureEventDto>(<any>null);
  }

  /**
   * Updates fixture event for current club user [Club League].
   * @param model (optional) Update Fixture Event Model
   * @return Returns fixture event details
   */
  put(
    model: UpdateFixtureEventDto | null | undefined,
  ): Observable<FixtureEventDetailsDto> {
    let url_ = this.baseUrl + "/api/Fixture";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPut(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPut(<any>response_);
          } catch (e) {
            return <Observable<FixtureEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<FixtureEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPut(
    response: HttpResponseBase,
  ): Observable<FixtureEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = FixtureEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FixtureEventDetailsDto>(<any>null);
  }

  /**
   * Returns list of season match rounds with matches for league [League].
   * @param nonLiveInUK (optional)
   * @return Returns list of season match rounds with matches
   */
  getAllForLeague(
    matchRound: number | null,
    nonLiveInUK: boolean | undefined,
  ): Observable<WeekDto[]> {
    let url_ = this.baseUrl + "/api/Fixture/getAllForLeague?";
    if (matchRound === undefined)
      throw new Error("The parameter 'matchRound' must be defined.");
    else if (matchRound !== null)
      url_ += "matchRound=" + encodeURIComponent("" + matchRound) + "&";
    if (nonLiveInUK === null)
      throw new Error("The parameter 'nonLiveInUK' cannot be null.");
    else if (nonLiveInUK !== undefined)
      url_ += "nonLiveInUK=" + encodeURIComponent("" + nonLiveInUK) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAllForLeague(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAllForLeague(<any>response_);
          } catch (e) {
            return <Observable<WeekDto[]>>(<any>throwError(() => e));
          }
        } else return <Observable<WeekDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetAllForLeague(
    response: HttpResponseBase,
  ): Observable<WeekDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(WeekDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<WeekDto[]>([]);
  }

  /**
   * Returns list of season match rounds with matches for broadcaster [Broadcaster].
   * @param nonLiveInUK (optional)
   * @return Returns list of season match rounds with matches
   */
  getAllForBroadcaster(
    matchRound: number | null,
    nonLiveInUK: boolean | undefined,
  ): Observable<WeekDto[]> {
    let url_ = this.baseUrl + "/api/Fixture/getAllForBroadcaster?";
    if (matchRound === undefined)
      throw new Error("The parameter 'matchRound' must be defined.");
    else if (matchRound !== null)
      url_ += "matchRound=" + encodeURIComponent("" + matchRound) + "&";
    if (nonLiveInUK === null)
      throw new Error("The parameter 'nonLiveInUK' cannot be null.");
    else if (nonLiveInUK !== undefined)
      url_ += "nonLiveInUK=" + encodeURIComponent("" + nonLiveInUK) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAllForBroadcaster(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAllForBroadcaster(<any>response_);
          } catch (e) {
            return <Observable<WeekDto[]>>(<any>throwError(() => e));
          }
        } else return <Observable<WeekDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetAllForBroadcaster(
    response: HttpResponseBase,
  ): Observable<WeekDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(WeekDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<WeekDto[]>([]);
  }

  /**
   * Returns Fixture event details by event Id [Club League Broadcaster].
   * @param id Event Id. Guid
   * @return Returns list of season match rounds with matches
   */
  get(id: string): Observable<FixtureEventDetailsDto> {
    let url_ = this.baseUrl + "/api/Fixture/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<FixtureEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<FixtureEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<FixtureEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = FixtureEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FixtureEventDetailsDto>(<any>null);
  }

  /**
   * Returns Events for Fixture by Fixture Id [Club League Broadcaster].
   * @param id Match id. An integer
   * @param nonLiveInUK (optional)
   * @return Returns fixture events details
   */
  getMatchEvents(
    id: number,
    nonLiveInUK: boolean | undefined,
  ): Observable<FixtureEventDetailsDto[]> {
    let url_ = this.baseUrl + "/api/Fixture/getMatchEvents/{id}?";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (nonLiveInUK === null)
      throw new Error("The parameter 'nonLiveInUK' cannot be null.");
    else if (nonLiveInUK !== undefined)
      url_ += "nonLiveInUK=" + encodeURIComponent("" + nonLiveInUK) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetMatchEvents(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetMatchEvents(<any>response_);
          } catch (e) {
            return <Observable<FixtureEventDetailsDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<FixtureEventDetailsDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetMatchEvents(
    response: HttpResponseBase,
  ): Observable<FixtureEventDetailsDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(FixtureEventDetailsDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FixtureEventDetailsDto[]>([]);
  }

  /**
   * Returns Match info by Fixture Id [Club].
   * @param id Match id. An integer
   * @return Returns Match info
   */
  getMatchInfo(id: string): Observable<MatchInfoDto> {
    let url_ = this.baseUrl + "/api/Fixture/getMatchInfo/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetMatchInfo(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetMatchInfo(<any>response_);
          } catch (e) {
            return <Observable<MatchInfoDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<MatchInfoDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetMatchInfo(
    response: HttpResponseBase,
  ): Observable<MatchInfoDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MatchInfoDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<MatchInfoDto>(<any>null);
  }
}

@Injectable()
export class FixtureParticipantClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Get participant for broadcaster [No Role].
   * @param type Create participant model
   * @return Returns created participant
   */
  get(type: ParticipantType): Observable<ParticipantDto[]> {
    let url_ = this.baseUrl + "/api/FixtureParticipant/{type}";
    if (type === undefined || type === null)
      throw new Error("The parameter 'type' must be defined.");
    url_ = url_.replace("{type}", encodeURIComponent("" + type));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }

  /**
   * Creates new participant for broadcaster [No Role].
   * @param model (optional) Create participant model
   * @return Returns created participant
   */
  create(
    model: CreateParticipantDto | null | undefined,
  ): Observable<ParticipantDto> {
    let url_ = this.baseUrl + "/api/FixtureParticipant";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<ParticipantDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ParticipantDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto>(<any>null);
  }
}

@Injectable()
export class FixtureRequestClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns fixture request details by request id [No Role].
   * @param id Request id. Guid
   * @return Returns fixture request details
   */
  get(id: string): Observable<RequestDetailsDto> {
    let url_ = this.baseUrl + "/api/FixtureRequest/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<RequestDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<RequestDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<RequestDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = RequestDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<RequestDetailsDto>(<any>null);
  }

  /**
   * Creates new request for fixture [Broadcaster].
   * @param dto (optional) Create request model
   * @return Returns fixture event data
   */
  post(
    dto: CreateFixtureRequestDto | null | undefined,
  ): Observable<FixtureEventDetailsDto> {
    let url_ = this.baseUrl + "/api/FixtureRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<FixtureEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<FixtureEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPost(
    response: HttpResponseBase,
  ): Observable<FixtureEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = FixtureEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FixtureEventDetailsDto>(<any>null);
  }

  /**
   * Updates request details for fixture [League Broadcaster].
   * @param dto (optional) Update request model
   * @return Returns fixture event data
   */
  put(
    dto: UpdateFixtureRequestDto | null | undefined,
  ): Observable<FixtureEventDetailsDto> {
    let url_ = this.baseUrl + "/api/FixtureRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPut(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPut(<any>response_);
          } catch (e) {
            return <Observable<FixtureEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<FixtureEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPut(
    response: HttpResponseBase,
  ): Observable<FixtureEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = FixtureEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FixtureEventDetailsDto>(<any>null);
  }
}

@Injectable()
export class ImportClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Method for import bpp/training data. Allows xslx file as body param [No Role].
   * @return Returns result of Import operation with description of fails during the import
   */
  import(): Observable<ImportResultDto> {
    let url_ = this.baseUrl + "/api/Import";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processImport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processImport(<any>response_);
          } catch (e) {
            return <Observable<ImportResultDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ImportResultDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processImport(
    response: HttpResponseBase,
  ): Observable<ImportResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ImportResultDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ImportResultDto>(<any>null);
  }
}

@Injectable()
export class InterviewsClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Return interviews [League, Broadcaster, Club].
   * @return Interviews list
   */
  getAll(past: boolean): Observable<InterviewDto[]> {
    let url_ = this.baseUrl + "/api/Interviews/GetAll?";
    if (past === undefined || past === null)
      throw new Error(
        "The parameter 'past' must be defined and cannot be null.",
      );
    else url_ += "past=" + encodeURIComponent("" + past) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<InterviewDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<InterviewDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<InterviewDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(InterviewDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<InterviewDto[]>([]);
  }

  /**
   * Return interview details for id [League, Broadcaster].
   */
  getDetails(id: string): Observable<ResultOfInterviewDetailsDto> {
    let url_ = this.baseUrl + "/api/Interviews/GetDetails?";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined and cannot be null.");
    else url_ += "id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetDetails(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetDetails(<any>response_);
          } catch (e) {
            return <Observable<ResultOfInterviewDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ResultOfInterviewDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetDetails(
    response: HttpResponseBase,
  ): Observable<ResultOfInterviewDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ResultOfInterviewDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ResultOfInterviewDetailsDto>(<any>null);
  }

  /**
   * Create interview [League]
   * @param model (optional)
   */
  create(
    model: InterviewDetailsDto | null | undefined,
  ): Observable<ResultOfInterviewDetailsDto> {
    let url_ = this.baseUrl + "/api/Interviews/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<ResultOfInterviewDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ResultOfInterviewDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<ResultOfInterviewDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ResultOfInterviewDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ResultOfInterviewDetailsDto>(<any>null);
  }

  /**
   * Update interview [League]
   * @param model (optional)
   */
  update(
    model: InterviewDetailsDto | null | undefined,
  ): Observable<ResultOfInterviewDetailsDto> {
    let url_ = this.baseUrl + "/api/Interviews/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<ResultOfInterviewDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ResultOfInterviewDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<ResultOfInterviewDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ResultOfInterviewDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ResultOfInterviewDetailsDto>(<any>null);
  }
}

@Injectable()
export class InterviewsRequestClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Create interviews request
   * @param model (optional)
   */
  create(
    model: CreateInterviewsRequestDto | null | undefined,
  ): Observable<ValidationResultOfInterviewsRequestDto> {
    let url_ = this.baseUrl + "/api/InterviewsRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<ValidationResultOfInterviewsRequestDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ValidationResultOfInterviewsRequestDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<ValidationResultOfInterviewsRequestDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 =
            ValidationResultOfInterviewsRequestDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ValidationResultOfInterviewsRequestDto>(<any>null);
  }

  /**
   * Update interviews request
   * @param model (optional)
   */
  update(
    model: InterviewsRequestDto | null | undefined,
  ): Observable<ValidationResultOfInterviewsRequestDto> {
    let url_ = this.baseUrl + "/api/InterviewsRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<ValidationResultOfInterviewsRequestDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ValidationResultOfInterviewsRequestDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<ValidationResultOfInterviewsRequestDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 =
            ValidationResultOfInterviewsRequestDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ValidationResultOfInterviewsRequestDto>(<any>null);
  }
}

@Injectable()
export class InterviewTypesClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Return interview types [League, Broadcaster, Club].
   * @return Interview types list
   */
  getAll(): Observable<InterviewTypeDto[]> {
    let url_ = this.baseUrl + "/api/InterviewTypes/GetAll";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<InterviewTypeDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<InterviewTypeDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<InterviewTypeDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(InterviewTypeDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<InterviewTypeDto[]>([]);
  }
}

@Injectable()
export class LanguageClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Enpoint to get list of languages [No Role].
   * @return Returns list of languages in app. Language model
   */
  get(): Observable<LanguageDto[]> {
    let url_ = this.baseUrl + "/api/Language";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<LanguageDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LanguageDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGet(response: HttpResponseBase): Observable<LanguageDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(LanguageDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LanguageDto[]>([]);
  }

  /**
   * Creates new language in application [No Role].
   * @param model (optional) A create language model.
   * @return Returns created language
   */
  create(model: CreateLanguageDto | null | undefined): Observable<LanguageDto> {
    let url_ = this.baseUrl + "/api/Language";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<LanguageDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LanguageDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processCreate(response: HttpResponseBase): Observable<LanguageDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = LanguageDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LanguageDto>(<any>null);
  }
}

@Injectable()
export class LocationClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of locations depends of param [Club League].
   * @param clubId Optional parameter to filter locations by club id. An integer
   * @return Returns list of locations.
   */
  getAll(clubId: number): Observable<LocationDto[]> {
    let url_ = this.baseUrl + "/api/Location/{clubId}";
    if (clubId === undefined || clubId === null)
      throw new Error("The parameter 'clubId' must be defined.");
    url_ = url_.replace("{clubId}", encodeURIComponent("" + clubId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<LocationDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<LocationDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(LocationDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationDto[]>([]);
  }

  /**
   * Return list of locations [League]
   * @return Returns list of locations.
   */
  get(): Observable<LocationDto[]> {
    let url_ = this.baseUrl + "/api/Location/getByType";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<LocationDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGet(response: HttpResponseBase): Observable<LocationDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(LocationDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationDto[]>([]);
  }

  /**
   * Returns location details by Id [Club League].
   * @param id Guid. Id of location in application
   * @return Returns Location Details.
   */
  getById(id: string): Observable<LocationDetailsDto> {
    let url_ = this.baseUrl + "/api/Location/getById/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetById(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetById(<any>response_);
          } catch (e) {
            return <Observable<LocationDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetById(
    response: HttpResponseBase,
  ): Observable<LocationDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = LocationDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationDetailsDto>(<any>null);
  }

  /**
   * Creates location in application by input model [Club League].
   * @param model (optional) Create Location Model
   * @return Returns Location (Not detailed).
   */
  create(model: CreateLocationDto | null | undefined): Observable<LocationDto> {
    let url_ = this.baseUrl + "/api/Location";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<LocationDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processCreate(response: HttpResponseBase): Observable<LocationDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = LocationDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationDto>(<any>null);
  }

  /**
   * Updates location in application by input model [Club League].
   * @param model (optional) Location Details Model
   * @return Returns Location (Detailed).
   */
  update(
    model: LocationDetailsDto | null | undefined,
  ): Observable<LocationDetailsDto> {
    let url_ = this.baseUrl + "/api/Location";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<LocationDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<LocationDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = LocationDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationDetailsDto>(<any>null);
  }

  /**
   * Delete location from application by id [Club League].
   * @param id Guid. Id of Location
   * @return Returns Location id that was deleted.
   */
  delete(id: string): Observable<string> {
    let url_ = this.baseUrl + "/api/Location/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("delete", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processDelete(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processDelete(<any>response_);
          } catch (e) {
            return <Observable<string>>(<any>throwError(() => e));
          }
        } else return <Observable<string>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processDelete(response: HttpResponseBase): Observable<string> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<string>(<any>null);
  }
}

@Injectable()
export class LocationTypeClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of location types
   */
  getAll(): Observable<LocationTypeDto[]> {
    let url_ = this.baseUrl + "/api/LocationType";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<LocationTypeDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationTypeDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<LocationTypeDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(LocationTypeDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationTypeDto[]>([]);
  }

  /**
   * Returns list of available location types (according to the rules defined in the location types)
   */
  getAllAvailable(clubId: number | null): Observable<LocationTypeDto[]> {
    let url_ = this.baseUrl + "/api/LocationType/getAllAvailable?";
    if (clubId === undefined)
      throw new Error("The parameter 'clubId' must be defined.");
    else if (clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAllAvailable(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAllAvailable(<any>response_);
          } catch (e) {
            return <Observable<LocationTypeDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<LocationTypeDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAllAvailable(
    response: HttpResponseBase,
  ): Observable<LocationTypeDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(LocationTypeDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<LocationTypeDto[]>([]);
  }
}

@Injectable()
export class LoggerClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns Application Insights Instrumentation key [No Role].
   * @return Returns Application Insights Instrumentation key
   */
  getInstrumentationKey(): Observable<string> {
    let url_ = this.baseUrl + "/api/Logger/GetInstrumentationKey";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetInstrumentationKey(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetInstrumentationKey(<any>response_);
          } catch (e) {
            return <Observable<string>>(<any>throwError(() => e));
          }
        } else return <Observable<string>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetInstrumentationKey(
    response: HttpResponseBase,
  ): Observable<string> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<string>(<any>null);
  }
}

@Injectable()
export class MatchClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Get all matches info [League, Broadcaster]
   */
  getAll(): Observable<MatchExtInfoDto[]> {
    let url_ = this.baseUrl + "/api/Match/GetAll";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<MatchExtInfoDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<MatchExtInfoDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<MatchExtInfoDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(MatchExtInfoDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<MatchExtInfoDto[]>([]);
  }

  /**
   * Update a match details [League]
   * @param location (optional)
   */
  update(
    location: MatchDetailsDto | null | undefined,
  ): Observable<ResultOfMatchDetailsDto> {
    let url_ = this.baseUrl + "/api/Match";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(location);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<ResultOfMatchDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ResultOfMatchDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<ResultOfMatchDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ResultOfMatchDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ResultOfMatchDetailsDto>(<any>null);
  }

  /**
   * HACK (it should call usermanagement api, but at this stage there is a problem related to authentication): Get the flag "NonLiveInUKEnabled" for company id
   */
  isCompanyEnabledForNonLiveInUK(companyId: string): Observable<boolean> {
    let url_ =
      this.baseUrl +
      "/api/Match/is-company-enabled-for-non-live-in-uk/{companyId}";
    if (companyId === undefined || companyId === null)
      throw new Error("The parameter 'companyId' must be defined.");
    url_ = url_.replace("{companyId}", encodeURIComponent("" + companyId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processIsCompanyEnabledForNonLiveInUK(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processIsCompanyEnabledForNonLiveInUK(<any>response_);
          } catch (e) {
            return <Observable<boolean>>(<any>throwError(() => e));
          }
        } else return <Observable<boolean>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processIsCompanyEnabledForNonLiveInUK(
    response: HttpResponseBase,
  ): Observable<boolean> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<boolean>(<any>null);
  }

  getMatchRoundNumbers(): Observable<MatchRoundNumbersDto> {
    let url_ = this.baseUrl + "/api/Match/MatchRounds";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetMatchRoundNumbers(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetMatchRoundNumbers(<any>response_);
          } catch (e) {
            return <Observable<MatchRoundNumbersDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<MatchRoundNumbersDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetMatchRoundNumbers(
    response: HttpResponseBase,
  ): Observable<MatchRoundNumbersDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<MatchRoundNumbersDto>(<any>null);
  }
}

@Injectable()
export class MigrationClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of season matches for club [Club].
   * @return Returns list of season match rounds with matches
   */
  updateUserData(): Observable<boolean> {
    let url_ = this.baseUrl + "/api/migration/userDataMigration";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdateUserData(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdateUserData(<any>response_);
          } catch (e) {
            return <Observable<boolean>>(<any>throwError(() => e));
          }
        } else return <Observable<boolean>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processUpdateUserData(
    response: HttpResponseBase,
  ): Observable<boolean> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<boolean>(<any>null);
  }
}

@Injectable()
export class NoteClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Separate endpoint to save club request notes after BPP/Training was closed [Club].
   * @param note (optional) Save Note Model
   * @return Returns saved note (NoteDto)
   */
  saveClubRequestNote(
    note: SaveNoteDto | null | undefined,
  ): Observable<NoteDto> {
    let url_ = this.baseUrl + "/api/notes/SaveClubRequestComment";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(note);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSaveClubRequestNote(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSaveClubRequestNote(<any>response_);
          } catch (e) {
            return <Observable<NoteDto>>(<any>throwError(() => e));
          }
        } else return <Observable<NoteDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processSaveClubRequestNote(
    response: HttpResponseBase,
  ): Observable<NoteDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = NoteDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<NoteDto>(<any>null);
  }

  /**
   * Separate endpoint to save admin notes for BPP/Training after it was closed [League].
   * @param note (optional) Save Note Model
   * @return Returns saved note (NoteDto)
   */
  saveLeagueAdminNote(
    note: SaveNoteDto | null | undefined,
  ): Observable<NoteDto> {
    let url_ = this.baseUrl + "/api/notes/SaveLeagueAdminBPPComment";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(note);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSaveLeagueAdminNote(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSaveLeagueAdminNote(<any>response_);
          } catch (e) {
            return <Observable<NoteDto>>(<any>throwError(() => e));
          }
        } else return <Observable<NoteDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processSaveLeagueAdminNote(
    response: HttpResponseBase,
  ): Observable<NoteDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = NoteDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException("\nor\n", status, _responseText, _headers);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<NoteDto>(<any>null);
  }
}

@Injectable()
export class PeopleClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns Players list by current user club id [No Role].
   * @return Returns Players list
   */
  getTeamMembers(): Observable<PlayerListDto> {
    let url_ = this.baseUrl + "/api/People";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetTeamMembers(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetTeamMembers(<any>response_);
          } catch (e) {
            return <Observable<PlayerListDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<PlayerListDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetTeamMembers(
    response: HttpResponseBase,
  ): Observable<PlayerListDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = PlayerListDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<PlayerListDto>(<any>null);
  }

  /**
   * Returns Players list by club Id [No Role].
   * @param id Club id. An integer
   * @return Returns Players list
   */
  getById(id: number): Observable<PlayerListDto> {
    let url_ = this.baseUrl + "/api/People/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetById(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetById(<any>response_);
          } catch (e) {
            return <Observable<PlayerListDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<PlayerListDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetById(
    response: HttpResponseBase,
  ): Observable<PlayerListDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = PlayerListDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<PlayerListDto>(<any>null);
  }
}

@Injectable()
export class ReportClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns Person Report data by club id [Club].
   * @param clubId (optional) Club id. An integer
   * @return Returns Person Report data
   */
  getPersonsReport(
    clubId: number | null | undefined,
  ): Observable<PersonReportDataDto[]> {
    let url_ = this.baseUrl + "/api/Report/Persons?";
    if (clubId !== undefined && clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetPersonsReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetPersonsReport(<any>response_);
          } catch (e) {
            return <Observable<PersonReportDataDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<PersonReportDataDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetPersonsReport(
    response: HttpResponseBase,
  ): Observable<PersonReportDataDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(PersonReportDataDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<PersonReportDataDto[]>([]);
  }

  /**
   * Returns Person Report excel file by club id [Club League].
   * @param clubId (optional) Club id. An integer
   * @return Returns Person Report excel file
   */
  getPersonsExcelReport(
    clubId: number | null | undefined,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/PersonsExcel?";
    if (clubId !== undefined && clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetPersonsExcelReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetPersonsExcelReport(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetPersonsExcelReport(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Returns Person Report pdf file by club id (not implemented yet) [No Role].
   * @param clubId (optional) Club id. An integer
   * @return Returns Person Report pdf file
   */
  getPersonsPdfReport(
    clubId: number | null | undefined,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/PersonsPdf?";
    if (clubId !== undefined && clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetPersonsPdfReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetPersonsPdfReport(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetPersonsPdfReport(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Returns Fixture requests Report excel file by match id and fixture event id [Club League].
   * @param matchId Match id. An integer
   * @param fixtureId Fixture event id. Guid
   * @param nonLiveInUK (optional) Consider live or non-live matches
   * @return Returns Fixture requests Report excel file
   */
  getFixtureRequestsReportExcel(
    matchId: number,
    fixtureId: string,
    nonLiveInUK: boolean | undefined,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/FixtureRequestsExcel?";
    if (matchId === undefined || matchId === null)
      throw new Error(
        "The parameter 'matchId' must be defined and cannot be null.",
      );
    else url_ += "matchId=" + encodeURIComponent("" + matchId) + "&";
    if (fixtureId === undefined || fixtureId === null)
      throw new Error(
        "The parameter 'fixtureId' must be defined and cannot be null.",
      );
    else url_ += "fixtureId=" + encodeURIComponent("" + fixtureId) + "&";
    if (nonLiveInUK === null)
      throw new Error("The parameter 'nonLiveInUK' cannot be null.");
    else if (nonLiveInUK !== undefined)
      url_ += "nonLiveInUK=" + encodeURIComponent("" + nonLiveInUK) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetFixtureRequestsReportExcel(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetFixtureRequestsReportExcel(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetFixtureRequestsReportExcel(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Returns Fixture requests Report word file by match id and fixture event id [Club League].
   * @param matchId Match id. An integer
   * @param fixtureId Fixture event id. Guid
   * @param nonLiveInUK (optional)
   * @return Returns Fixture requests Report word file
   */
  getFixtureRequestsReportWord(
    matchId: number,
    fixtureId: string,
    nonLiveInUK: boolean | undefined,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/FixtureRequestsWord?";
    if (matchId === undefined || matchId === null)
      throw new Error(
        "The parameter 'matchId' must be defined and cannot be null.",
      );
    else url_ += "matchId=" + encodeURIComponent("" + matchId) + "&";
    if (fixtureId === undefined || fixtureId === null)
      throw new Error(
        "The parameter 'fixtureId' must be defined and cannot be null.",
      );
    else url_ += "fixtureId=" + encodeURIComponent("" + fixtureId) + "&";
    if (nonLiveInUK === null)
      throw new Error("The parameter 'nonLiveInUK' cannot be null.");
    else if (nonLiveInUK !== undefined)
      url_ += "nonLiveInUK=" + encodeURIComponent("" + nonLiveInUK) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetFixtureRequestsReportWord(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetFixtureRequestsReportWord(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetFixtureRequestsReportWord(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Returns Training requests Report excel file by training id [Club League].
   * @param trainingId Training id. Guid
   * @return Returns Training requests Report excel file
   */
  getTrainingRequestsReportExcel(
    trainingId: string,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/TrainingRequestsExcel?";
    if (trainingId === undefined || trainingId === null)
      throw new Error(
        "The parameter 'trainingId' must be defined and cannot be null.",
      );
    else url_ += "trainingId=" + encodeURIComponent("" + trainingId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetTrainingRequestsReportExcel(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetTrainingRequestsReportExcel(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetTrainingRequestsReportExcel(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Returns Training requests Report word file by training id [Club League].
   * @param trainingId Training id. Guid
   * @return Returns Training requests Report word file
   */
  getTrainingRequestsReportWord(
    trainingId: string,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/TrainingRequestsWord?";
    if (trainingId === undefined || trainingId === null)
      throw new Error(
        "The parameter 'trainingId' must be defined and cannot be null.",
      );
    else url_ += "trainingId=" + encodeURIComponent("" + trainingId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetTrainingRequestsReportWord(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetTrainingRequestsReportWord(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetTrainingRequestsReportWord(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  /**
   * Return Content Sessions Types data [League].
   * @return Return Content Sessions Types data
   */
  getContentSessionTypesReport(): Observable<ContentSessionTypeDataDto[]> {
    let url_ = this.baseUrl + "/api/Report/ContentSessionTypes";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetContentSessionTypesReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetContentSessionTypesReport(<any>response_);
          } catch (e) {
            return <Observable<ContentSessionTypeDataDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ContentSessionTypeDataDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetContentSessionTypesReport(
    response: HttpResponseBase,
  ): Observable<ContentSessionTypeDataDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ContentSessionTypeDataDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ContentSessionTypeDataDto[]>([]);
  }

  /**
   * Return Context Session Types Excel data [League].
   * @return Return Context Session Types Excel data
   */
  getContentSessionTypesExcelReport(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/ContentSessionTypesExcel";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetContentSessionTypesExcelReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetContentSessionTypesExcelReport(
              <any>response_,
            );
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetContentSessionTypesExcelReport(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  getInterviewsSummary(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/GetInterviewsSummary";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetInterviewsSummary(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetInterviewsSummary(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetInterviewsSummary(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  getInterviewsSummaryPdfReport(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/GetInterviewsSummaryPdfReport";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetInterviewsSummaryPdfReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetInterviewsSummaryPdfReport(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetInterviewsSummaryPdfReport(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  getInterviewsSummaryData(): Observable<InterviewsSummaryDataDto[]> {
    let url_ = this.baseUrl + "/api/Report/GetInterviewsSummary/Data";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetInterviewsSummaryData(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetInterviewsSummaryData(<any>response_);
          } catch (e) {
            return <Observable<InterviewsSummaryDataDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<InterviewsSummaryDataDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetInterviewsSummaryData(
    response: HttpResponseBase,
  ): Observable<InterviewsSummaryDataDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(InterviewsSummaryDataDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<InterviewsSummaryDataDto[]>([]);
  }

  getBroadcasterRequests(
    clubId: number | null | undefined,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/GetBroadcasterRequests?";
    if (clubId !== undefined && clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetBroadcasterRequests(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetBroadcasterRequests(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetBroadcasterRequests(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  getBroadcasterRequestsPdfReport(
    clubId: number | null | undefined,
  ): Observable<FileResponse | null> {
    let url_ = this.baseUrl + "/api/Report/GetBroadcasterRequestsPdfReport?";
    if (clubId !== undefined && clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetBroadcasterRequestsPdfReport(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetBroadcasterRequestsPdfReport(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetBroadcasterRequestsPdfReport(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }

  getBroadcasterRequestsData(
    clubId: number | null | undefined,
  ): Observable<BroadcasterRequestsDataDto[]> {
    let url_ = this.baseUrl + "/api/Report/GetBroadcasterRequests/Data?";
    if (clubId !== undefined && clubId !== null)
      url_ += "clubId=" + encodeURIComponent("" + clubId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetBroadcasterRequestsData(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetBroadcasterRequestsData(<any>response_);
          } catch (e) {
            return <Observable<BroadcasterRequestsDataDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<BroadcasterRequestsDataDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetBroadcasterRequestsData(
    response: HttpResponseBase,
  ): Observable<BroadcasterRequestsDataDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(BroadcasterRequestsDataDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<BroadcasterRequestsDataDto[]>([]);
  }
}

@Injectable()
export class TeamMembersClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  get(skip: number, take: number): Observable<PagedListOfTeamMemberDto> {
    let url_ = this.baseUrl + "/api/TeamMembers?";
    if (skip === undefined || skip === null)
      throw new Error(
        "The parameter 'skip' must be defined and cannot be null.",
      );
    else url_ += "skip=" + encodeURIComponent("" + skip) + "&";
    if (take === undefined || take === null)
      throw new Error(
        "The parameter 'take' must be defined and cannot be null.",
      );
    else url_ += "take=" + encodeURIComponent("" + take) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<PagedListOfTeamMemberDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<PagedListOfTeamMemberDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<PagedListOfTeamMemberDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = PagedListOfTeamMemberDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<PagedListOfTeamMemberDto>(<any>null);
  }

  post(
    model: UpdateTeamMemberDto | null | undefined,
  ): Observable<TeamMemberDto> {
    let url_ = this.baseUrl + "/api/TeamMembers";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<TeamMemberDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<TeamMemberDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processPost(response: HttpResponseBase): Observable<TeamMemberDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TeamMemberDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TeamMemberDto>(<any>null);
  }

  getByClubId(
    id: number,
    skip: number,
    take: number,
  ): Observable<PagedListOfTeamMemberDto> {
    let url_ = this.baseUrl + "/api/TeamMembers/getByClubId/{id}?";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (skip === undefined || skip === null)
      throw new Error(
        "The parameter 'skip' must be defined and cannot be null.",
      );
    else url_ += "skip=" + encodeURIComponent("" + skip) + "&";
    if (take === undefined || take === null)
      throw new Error(
        "The parameter 'take' must be defined and cannot be null.",
      );
    else url_ += "take=" + encodeURIComponent("" + take) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetByClubId(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetByClubId(<any>response_);
          } catch (e) {
            return <Observable<PagedListOfTeamMemberDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<PagedListOfTeamMemberDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetByClubId(
    response: HttpResponseBase,
  ): Observable<PagedListOfTeamMemberDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = PagedListOfTeamMemberDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<PagedListOfTeamMemberDto>(<any>null);
  }

  get2(memberId: number): Observable<ResultOfTeamMemberDto> {
    let url_ = this.baseUrl + "/api/TeamMembers/{memberId}";
    if (memberId === undefined || memberId === null)
      throw new Error("The parameter 'memberId' must be defined.");
    url_ = url_.replace("{memberId}", encodeURIComponent("" + memberId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet2(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet2(<any>response_);
          } catch (e) {
            return <Observable<ResultOfTeamMemberDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ResultOfTeamMemberDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet2(
    response: HttpResponseBase,
  ): Observable<ResultOfTeamMemberDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ResultOfTeamMemberDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ResultOfTeamMemberDto>(<any>null);
  }
}

@Injectable()
export class TrainingClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns trainings for current season [Club League Broadcaster].
   * @return Returns list of trainings
   */
  getAll(): Observable<TrainingDto[]> {
    let url_ = this.baseUrl + "/api/Training";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<TrainingDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<TrainingDto[]>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<TrainingDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(TrainingDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TrainingDto[]>([]);
  }

  /**
   * Creates new training for current club user [Club].
   * @param model (optional) Create Training Model
   * @return Returns training details
   */
  post(
    model: CreateTrainingDto | null | undefined,
  ): Observable<TrainingDetailsDto> {
    let url_ = this.baseUrl + "/api/Training";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<TrainingDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<TrainingDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPost(
    response: HttpResponseBase,
  ): Observable<TrainingDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TrainingDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TrainingDetailsDto>(<any>null);
  }

  /**
   * Updates new training for current club user [Club League].
   * @param model (optional) Update Training Model
   * @return Returns training details
   */
  put(
    model: UpdateTrainingDto | null | undefined,
  ): Observable<TrainingDetailsDto> {
    let url_ = this.baseUrl + "/api/Training";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPut(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPut(<any>response_);
          } catch (e) {
            return <Observable<TrainingDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<TrainingDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPut(
    response: HttpResponseBase,
  ): Observable<TrainingDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TrainingDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TrainingDetailsDto>(<any>null);
  }

  /**
   * Returns training details info by training Id [Club League Broadcaster].
   * @param id Training Id. Guid
   * @return Returns list of trainings
   */
  get(id: string): Observable<TrainingDetailsDto> {
    let url_ = this.baseUrl + "/api/Training/{id}";
    if (id === undefined || id === null)
      throw new Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<TrainingDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<TrainingDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<TrainingDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TrainingDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TrainingDetailsDto>(<any>null);
  }
}

@Injectable()
export class TrainingParticipantClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns list of participants for current broadcaster user [Broadcaster].
   * @return Returns list of participants
   */
  getAll(): Observable<ParticipantDto[]> {
    let url_ = this.baseUrl + "/api/TrainingParticipant";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetAll(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAll(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetAll(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }

  /**
   * Creates new participant for broadcaster [Broadcaster].
   * @param model (optional) Create participant model
   * @return Returns created participant
   */
  post(
    model: CreateParticipantDto | null | undefined,
  ): Observable<ParticipantDto> {
    let url_ = this.baseUrl + "/api/TrainingParticipant";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processPost(
    response: HttpResponseBase,
  ): Observable<ParticipantDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ParticipantDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto>(<any>null);
  }

  /**
   * Returns list of participants for current broadcaster user by selected type [Broadcaster].
   * @param type Participant Type
   * @return Returns list of participants
   */
  get(type: ParticipantType): Observable<ParticipantDto[]> {
    let url_ = this.baseUrl + "/api/TrainingParticipant/{type}";
    if (type === undefined || type === null)
      throw new Error("The parameter 'type' must be defined.");
    url_ = url_.replace("{type}", encodeURIComponent("" + type));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }

  /**
   * Returns list of participants by broadcaster id (For league) [League].
   * @param companyId Broadcaster company Id. String
   * @return Returns list of participants
   */
  getByCompany(companyId: string | null): Observable<ParticipantDto[]> {
    let url_ = this.baseUrl + "/api/TrainingParticipant/getByCompany?";
    if (companyId === undefined)
      throw new Error("The parameter 'companyId' must be defined.");
    else if (companyId !== null)
      url_ += "companyId=" + encodeURIComponent("" + companyId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetByCompany(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetByCompany(<any>response_);
          } catch (e) {
            return <Observable<ParticipantDto[]>>(<any>throwError(() => e));
          }
        } else
          return <Observable<ParticipantDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetByCompany(
    response: HttpResponseBase,
  ): Observable<ParticipantDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ParticipantDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ParticipantDto[]>([]);
  }
}

@Injectable()
export class TrainingRequestClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Creates new request for training [Broadcaster].
   * @param model (optional) Create training request model
   * @return Returns training data
   */
  post(
    model: CreateTrainingRequestDto | null | undefined,
  ): Observable<TrainingDetailsDto> {
    let url_ = this.baseUrl + "/api/TrainingRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPost(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPost(<any>response_);
          } catch (e) {
            return <Observable<TrainingDetailsDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<TrainingDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processPost(
    response: HttpResponseBase,
  ): Observable<TrainingDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TrainingDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TrainingDetailsDto>(<any>null);
  }

  /**
   * Updates new request for training [League Broadcaster].
   * @param model (optional) Update training request model
   * @return Returns training data
   */
  put(
    model: UpdateTrainingRequestDto | null | undefined,
  ): Observable<TrainingDetailsDto> {
    let url_ = this.baseUrl + "/api/TrainingRequest";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processPut(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processPut(<any>response_);
          } catch (e) {
            return throwError(() => e);
          }
        } else return throwError(() => response_);
      }),
    );
  }

  protected processPut(
    response: HttpResponseBase,
  ): Observable<TrainingDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = TrainingDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 500) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "Unhandled Error message",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<TrainingDetailsDto>(<any>null);
  }
}

@Injectable()
export class UserClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  /**
   * Returns current user [No Role].
   * @return Returns current user
   */
  getUser(): Observable<UserDto | null> {
    let url_ = this.baseUrl + API_USER_BASE_URL;
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetUser(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetUser(<any>response_);
          } catch (e) {
            return <Observable<UserDto | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<UserDto | null>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processGetUser(
    response: HttpResponseBase,
  ): Observable<UserDto | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 ? UserDto.fromJS(resultData200) : <any>null;
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<UserDto | null>(<any>null);
  }

  /**
   * Returns current user details [No Role].
   * @return Returns current user details
   */
  getDetails(): Observable<UserDetails | null> {
    let url_ = this.baseUrl + API_USER_BASE_URL + "/Details";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetDetails(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetDetails(<any>response_);
          } catch (e) {
            return <Observable<UserDetails | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<UserDetails | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetDetails(
    response: HttpResponseBase,
  ): Observable<UserDetails | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200
            ? UserDetails.fromJS(resultData200)
            : <any>null;
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<UserDetails | null>(<any>null);
  }

  /**
   * Gets users applications which it assigned in Azure [League, Broadcaster]
   * @return Applications list for current users
   */
  getApplications(): Observable<ApplicationDto[] | null> {
    let url_ = this.baseUrl + API_USER_BASE_URL + "/Applications";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetApplications(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetApplications(<any>response_);
          } catch (e) {
            return <Observable<ApplicationDto[] | null>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ApplicationDto[] | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetApplications(
    response: HttpResponseBase,
  ): Observable<ApplicationDto[] | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ApplicationDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of();
  }

  /**
   * Updates current user notification types [Club League Broadcaster].
   * @param model (optional) Notification types list for update
   * @return Returns updated notification types list
   */
  updateNotificationTypes(
    model: UserNotificationType[] | null | undefined,
  ): Observable<UserNotificationType[]> {
    let url_ = this.baseUrl + API_USER_BASE_URL + "/UpdateNotificationTypes";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdateNotificationTypes(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdateNotificationTypes(<any>response_);
          } catch (e) {
            return <Observable<UserNotificationType[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<UserNotificationType[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdateNotificationTypes(
    response: HttpResponseBase,
  ): Observable<UserNotificationType[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(UserNotificationType.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<UserNotificationType[]>([]);
  }

  /**
   * Returns current user menu (not used) [No Role].
   * @return Returns current user menu
   */
  getUserMenu(): Observable<FileResponse | null> {
    let url_ = this.baseUrl + API_USER_BASE_URL + "/UserMenu";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetUserMenu(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetUserMenu(<any>response_);
          } catch (e) {
            return <Observable<FileResponse | null>>(<any>throwError(() => e));
          }
        } else
          return <Observable<FileResponse | null>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetUserMenu(
    response: HttpResponseBase,
  ): Observable<FileResponse | null> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200 || status === 206) {
      const contentDisposition = response.headers
        ? response.headers.get("content-disposition")
        : undefined;
      const fileNameMatch = contentDisposition
        ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition)
        : undefined;
      const fileName =
        fileNameMatch && fileNameMatch.length > 1
          ? fileNameMatch[1]
          : undefined;
      return of({
        fileName: fileName,
        data: <any>responseBlob,
        status: status,
        headers: _headers,
      });
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<FileResponse | null>(<any>null);
  }
}

@Injectable()
export class InterviewEventClient extends ClientBase {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(ConfigService) configuration: ConfigService,
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string,
  ) {
    super(configuration);
    this.http = http;
    this.baseUrl =
      baseUrl !== undefined && baseUrl !== null ? baseUrl : this.getBaseUrl("");
  }

  get(round: number): Observable<MatchInterviewEventDto[]> {
    let url_ = this.baseUrl + "/api/InterviewEvent?";
    if (round === undefined || round === null)
      throw new Error(
        "The parameter 'round' must be defined and cannot be null.",
      );
    else url_ += "round=" + encodeURIComponent("" + round) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGet(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGet(<any>response_);
          } catch (e) {
            return <Observable<MatchInterviewEventDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<MatchInterviewEventDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGet(
    response: HttpResponseBase,
  ): Observable<MatchInterviewEventDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(MatchInterviewEventDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<MatchInterviewEventDto[]>([]);
  }

  create(
    model: CreateInterviewEventDetailsDto | null | undefined,
  ): Observable<InterviewEventDetailsDto> {
    let url_ = this.baseUrl + "/api/InterviewEvent";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processCreate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processCreate(<any>response_);
          } catch (e) {
            return <Observable<InterviewEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<InterviewEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processCreate(
    response: HttpResponseBase,
  ): Observable<InterviewEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = InterviewEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<InterviewEventDetailsDto>(<any>null);
  }

  update(
    model: InterviewEventDetailsDto | null | undefined,
  ): Observable<InterviewEventDetailsDto> {
    let url_ = this.baseUrl + "/api/InterviewEvent";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("put", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processUpdate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processUpdate(<any>response_);
          } catch (e) {
            return <Observable<InterviewEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<InterviewEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processUpdate(
    response: HttpResponseBase,
  ): Observable<InterviewEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = InterviewEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<InterviewEventDetailsDto>(<any>null);
  }

  getForMatch(matchId: number): Observable<MatchInterviewEventDetailsDto> {
    let url_ = this.baseUrl + "/api/InterviewEvent/{matchId}";
    if (matchId === undefined || matchId === null)
      throw new Error("The parameter 'matchId' must be defined.");
    url_ = url_.replace("{matchId}", encodeURIComponent("" + matchId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetForMatch(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetForMatch(<any>response_);
          } catch (e) {
            return <Observable<MatchInterviewEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<MatchInterviewEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetForMatch(
    response: HttpResponseBase,
  ): Observable<MatchInterviewEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = MatchInterviewEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<MatchInterviewEventDetailsDto>(<any>null);
  }

  getDetailsForClub(
    matchId: number,
  ): Observable<ClubMatchInterviewEventDetailsDto> {
    let url_ = this.baseUrl + "/api/InterviewEvent/getDetailsForClub/{matchId}";
    if (matchId === undefined || matchId === null)
      throw new Error("The parameter 'matchId' must be defined.");
    url_ = url_.replace("{matchId}", encodeURIComponent("" + matchId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetDetailsForClub(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetDetailsForClub(<any>response_);
          } catch (e) {
            return <Observable<ClubMatchInterviewEventDetailsDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ClubMatchInterviewEventDetailsDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetDetailsForClub(
    response: HttpResponseBase,
  ): Observable<ClubMatchInterviewEventDetailsDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ClubMatchInterviewEventDetailsDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ClubMatchInterviewEventDetailsDto>(<any>null);
  }

  getMatchesForClub(): Observable<ClubMatchInterviewEventDto[]> {
    let url_ = this.baseUrl + "/api/InterviewEvent/getMatchesForClub";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("get", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processGetMatchesForClub(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetMatchesForClub(<any>response_);
          } catch (e) {
            return <Observable<ClubMatchInterviewEventDto[]>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ClubMatchInterviewEventDto[]>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processGetMatchesForClub(
    response: HttpResponseBase,
  ): Observable<ClubMatchInterviewEventDto[]> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          if (Array.isArray(resultData200)) {
            result200 = [] as any;
            for (let item of resultData200)
              result200.push(ClubMatchInterviewEventDto.fromJS(item));
          } else {
            result200 = <any>null;
          }
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ClubMatchInterviewEventDto[]>([]);
  }

  submitRequestForSlot(
    model: RequestToSlotDto | null | undefined,
  ): Observable<RequestToSlotDto> {
    let url_ = this.baseUrl + "/api/InterviewEvent/submitRequestForSlot";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processSubmitRequestForSlot(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSubmitRequestForSlot(<any>response_);
          } catch (e) {
            return <Observable<RequestToSlotDto>>(<any>throwError(() => e));
          }
        } else
          return <Observable<RequestToSlotDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processSubmitRequestForSlot(
    response: HttpResponseBase,
  ): Observable<RequestToSlotDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = RequestToSlotDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<RequestToSlotDto>(<any>null);
  }

  approveRequestForSlot(
    model: ApproveRequestToSlotDto | null | undefined,
  ): Observable<ApproveRequestToSlotDto> {
    let url_ = this.baseUrl + "/api/InterviewEvent/approveRequestForSlot";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("post", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processApproveRequestForSlot(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processApproveRequestForSlot(<any>response_);
          } catch (e) {
            return <Observable<ApproveRequestToSlotDto>>(
              (<any>throwError(() => e))
            );
          }
        } else
          return <Observable<ApproveRequestToSlotDto>>(
            (<any>throwError(() => response_))
          );
      }),
    );
  }

  protected processApproveRequestForSlot(
    response: HttpResponseBase,
  ): Observable<ApproveRequestToSlotDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ApproveRequestToSlotDto.fromJS(resultData200);
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<ApproveRequestToSlotDto>(<any>null);
  }

  removeRequestFromSlot(requestId: string | undefined): Observable<string> {
    let url_ = this.baseUrl + "/api/InterviewEvent/removeRequestFromSlot";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(requestId);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    };

    return from(this.transformOptions(options_)).pipe(
      mergeMap((transformedOptions_) => {
        return this.http.request("delete", url_, transformedOptions_);
      }),
      mergeMap((response_: any) => {
        return this.processRemoveRequestFromSlot(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processRemoveRequestFromSlot(<any>response_);
          } catch (e) {
            return <Observable<string>>(<any>throwError(() => e));
          }
        } else return <Observable<string>>(<any>throwError(() => response_));
      }),
    );
  }

  protected processRemoveRequestFromSlot(
    response: HttpResponseBase,
  ): Observable<string> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
          ? (<any>response).error
          : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = resultData200 !== undefined ? resultData200 : <any>null;
          return of(result200);
        }),
      );
    } else if (status === 404) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status === 401) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "A server side error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        mergeMap((_responseText) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers,
          );
        }),
      );
    }
    return of<string>(<any>null);
  }
}

export class AdditionalContentFixtureDto implements IAdditionalContentFixtureDto {
  eventId: string;
  type: AdditionalContentType;
  status: EventStatus;
  arrivalUtc: moment.Moment;
  clubId: number;
  clubName?: string | undefined;
  locationId: string;
  location?: string | undefined;
  totalRequests: number;
  managedRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  personId?: number | undefined;

  constructor(data?: IAdditionalContentFixtureDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.eventId = _data["eventId"];
      this.type = _data["type"];
      this.status = _data["status"];
      this.arrivalUtc = _data["arrivalUtc"]
        ? moment.parseZone(_data["arrivalUtc"].toString())
        : <any>undefined;
      this.clubId = _data["clubId"];
      this.clubName = _data["clubName"];
      this.locationId = _data["locationId"];
      this.location = _data["location"];
      this.totalRequests = _data["totalRequests"];
      this.managedRequests = _data["managedRequests"];
      this.approvedRequests = _data["approvedRequests"];
      this.rejectedRequests = _data["rejectedRequests"];
      this.participants = _data["participants"];
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      this.personId = _data["personId"];
    }
  }

  static fromJS(data: any): AdditionalContentFixtureDto {
    data = typeof data === "object" ? data : {};
    let result = new AdditionalContentFixtureDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["eventId"] = this.eventId;
    data["type"] = this.type;
    data["status"] = this.status;
    data["arrivalUtc"] = this.arrivalUtc
      ? this.arrivalUtc.toISOString(true)
      : <any>undefined;
    data["clubId"] = this.clubId;
    data["clubName"] = this.clubName;
    data["locationId"] = this.locationId;
    data["location"] = this.location;
    data["totalRequests"] = this.totalRequests;
    data["managedRequests"] = this.managedRequests;
    data["approvedRequests"] = this.approvedRequests;
    data["rejectedRequests"] = this.rejectedRequests;
    data["participants"] = this.participants;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    data["personId"] = this.personId;
    return data;
  }
}

export interface IAdditionalContentFixtureDto {
  eventId: string;
  type: AdditionalContentType;
  status: EventStatus;
  arrivalUtc: moment.Moment;
  clubId: number;
  clubName?: string | undefined;
  locationId: string;
  location?: string | undefined;
  totalRequests: number;
  managedRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  personId?: number | undefined;
}

export enum AdditionalContentType {
  PreSeason = 0,
  MidSeason = 1,
  Club = 2,
  TrainingGround = 3,
}

export enum EventStatus {
  Unknown = 0,
  Draft = 1,
  Submitted = 2,
  Approved = 3,
  Reject = 4,
}

export class AdditionalContentFixtureDetailsDto implements IAdditionalContentFixtureDetailsDto {
  eventId: string;
  status: EventStatus;
  type: AdditionalContentType;
  arrival: moment.Moment;
  start: moment.Moment;
  languages?: LanguageDto[] | undefined;
  clubNote?: NoteDto | undefined;
  leagueNote?: NoteDto | undefined;
  adminLeagueNote?: NoteDto | undefined;
  peoples?: PeopleDto[] | undefined;
  clubId: number;
  club?: string | undefined;
  locationId: string;
  location?: LocationDto | undefined;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  requests?: RequestDetailsDto[] | undefined;
  createdBy?: string | undefined;
  createdByName?: string | undefined;
  createOn: moment.Moment;
  updatedBy?: string | undefined;
  updatedByName?: string | undefined;
  updatedOn: moment.Moment;

  constructor(data?: IAdditionalContentFixtureDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.eventId = _data["eventId"];
      this.status = _data["status"];
      this.type = _data["type"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.adminLeagueNote = _data["adminLeagueNote"]
        ? NoteDto.fromJS(_data["adminLeagueNote"])
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PeopleDto.fromJS(item));
      }
      this.clubId = _data["clubId"];
      this.club = _data["club"];
      this.locationId = _data["locationId"];
      this.location = _data["location"]
        ? LocationDto.fromJS(_data["location"])
        : <any>undefined;
      this.participants = _data["participants"];
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      if (Array.isArray(_data["requests"])) {
        this.requests = [] as any;
        for (let item of _data["requests"])
          this.requests.push(RequestDetailsDto.fromJS(item));
      }
      this.createdBy = _data["createdBy"];
      this.createdByName = _data["createdByName"];
      this.createOn = _data["createOn"]
        ? moment.parseZone(_data["createOn"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedByName = _data["updatedByName"];
      this.updatedOn = _data["updatedOn"]
        ? moment.parseZone(_data["updatedOn"].toString())
        : <any>undefined;
    }
  }

  static fromJS(data: any): AdditionalContentFixtureDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new AdditionalContentFixtureDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["eventId"] = this.eventId;
    data["status"] = this.status;
    data["type"] = this.type;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["adminLeagueNote"] = this.adminLeagueNote
      ? this.adminLeagueNote.toJSON()
      : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    data["clubId"] = this.clubId;
    data["club"] = this.club;
    data["locationId"] = this.locationId;
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    data["participants"] = this.participants;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    if (Array.isArray(this.requests)) {
      data["requests"] = [];
      for (let item of this.requests) data["requests"].push(item.toJSON());
    }
    data["createdBy"] = this.createdBy;
    data["createdByName"] = this.createdByName;
    data["createOn"] = this.createOn
      ? this.createOn.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedByName"] = this.updatedByName;
    data["updatedOn"] = this.updatedOn
      ? this.updatedOn.toISOString(true)
      : <any>undefined;
    return data;
  }
}

export interface IAdditionalContentFixtureDetailsDto {
  eventId: string;
  status: EventStatus;
  type: AdditionalContentType;
  arrival: moment.Moment;
  start: moment.Moment;
  languages?: LanguageDto[] | undefined;
  clubNote?: NoteDto | undefined;
  leagueNote?: NoteDto | undefined;
  adminLeagueNote?: NoteDto | undefined;
  peoples?: PeopleDto[] | undefined;
  clubId: number;
  club?: string | undefined;
  locationId: string;
  location?: LocationDto | undefined;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  requests?: RequestDetailsDto[] | undefined;
  createdBy?: string | undefined;
  createdByName?: string | undefined;
  createOn: moment.Moment;
  updatedBy?: string | undefined;
  updatedByName?: string | undefined;
  updatedOn: moment.Moment;
}

/** Language model */
export class LanguageDto implements ILanguageDto {
  /** Language id */
  languageId: string;
  /** Language name */
  name?: string | undefined;
  /** Flag indicates that laguage should be at the top of the list */
  isPrimary: boolean;

  constructor(data?: ILanguageDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.languageId = _data["languageId"];
      this.name = _data["name"];
      this.isPrimary = _data["isPrimary"];
    }
  }

  static fromJS(data: any): LanguageDto {
    data = typeof data === "object" ? data : {};
    let result = new LanguageDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["languageId"] = this.languageId;
    data["name"] = this.name;
    data["isPrimary"] = this.isPrimary;
    return data;
  }
}

/** Language model */
export interface ILanguageDto {
  /** Language id */
  languageId: string;
  /** Language name */
  name?: string | undefined;
  /** Flag indicates that laguage should be at the top of the list */
  isPrimary: boolean;
}

/** Note information model */
export class NoteDto implements INoteDto {
  /** Note id */
  id: string;
  /** UserModel id who made a note */
  user?: string | undefined;
  /** UserModel name who made a note */
  userName?: string | undefined;
  /** Date of note */
  date: moment.Moment;
  /** Text of note */
  message?: string | undefined;
  /** Note status */
  status: NoteStatus;

  constructor(data?: INoteDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.user = _data["user"];
      this.userName = _data["userName"];
      this.date = _data["date"]
        ? moment.parseZone(_data["date"].toString())
        : <any>undefined;
      this.message = _data["message"];
      this.status = _data["status"];
    }
  }

  static fromJS(data: any): NoteDto {
    data = typeof data === "object" ? data : {};
    let result = new NoteDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["user"] = this.user;
    data["userName"] = this.userName;
    data["date"] = this.date ? this.date.toISOString(true) : <any>undefined;
    data["message"] = this.message;
    data["status"] = this.status;
    return data;
  }
}

/** Note information model */
export interface INoteDto {
  /** Note id */
  id: string;
  /** UserModel id who made a note */
  user?: string | undefined;
  /** UserModel name who made a note */
  userName?: string | undefined;
  /** Date of note */
  date: moment.Moment;
  /** Text of note */
  message?: string | undefined;
  /** Note status */
  status: NoteStatus;
}

export enum NoteStatus {
  Draft = 0,
  Submitted = 1,
}

/** People intermediate model */
export class PeopleDto implements IPeopleDto {
  personId: number;
  memberType: MemberTypeDto;

  constructor(data?: IPeopleDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.personId = _data["personId"];
      this.memberType = _data["memberType"];
    }
  }

  static fromJS(data: any): PeopleDto {
    data = typeof data === "object" ? data : {};
    let result = new PeopleDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["personId"] = this.personId;
    data["memberType"] = this.memberType;
    return data;
  }
}

/** People intermediate model */
export interface IPeopleDto {
  personId: number;
  memberType: MemberTypeDto;
}

export enum MemberTypeDto {
  Player = 0,
  Staff = 1,
}

/** Location common info */
export class LocationDto implements ILocationDto {
  /** Location id */
  locationId: string;
  /** Location title */
  title?: string | undefined;
  /** Location address string 1 */
  address1?: string | undefined;
  /** Location address string 2 */
  address2?: string | undefined;
  /** Location city/town */
  townCity: string;
  /** Location country */
  country: string;
  /** Location postcode */
  postcode: string;
  /** Location time zone id (not used) */
  timeZoneId?: string | undefined;
  /** Club id which location belongLocation */
  clubId: number;
  /** Location type */
  type: LocationTypes;

  constructor(data?: ILocationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.locationId = _data["locationId"];
      this.title = _data["title"];
      this.address1 = _data["address1"];
      this.address2 = _data["address2"];
      this.townCity = _data["townCity"];
      this.country = _data["country"];
      this.postcode = _data["postcode"];
      this.timeZoneId = _data["timeZoneId"];
      this.clubId = _data["clubId"];
      this.type = _data["type"];
    }
  }

  static fromJS(data: any): LocationDto {
    data = typeof data === "object" ? data : {};
    let result = new LocationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["locationId"] = this.locationId;
    data["title"] = this.title;
    data["address1"] = this.address1;
    data["address2"] = this.address2;
    data["townCity"] = this.townCity;
    data["country"] = this.country;
    data["postcode"] = this.postcode;
    data["timeZoneId"] = this.timeZoneId;
    data["clubId"] = this.clubId;
    data["type"] = this.type;
    return data;
  }
}

/** Location common info */
export interface ILocationDto {
  /** Location id */
  locationId: string;
  /** Location title */
  title?: string | undefined;
  /** Location address string 1 */
  address1?: string | undefined;
  /** Location address string 2 */
  address2?: string | undefined;
  /** Location city/town */
  townCity: string;
  /** Location country */
  country: string;
  /** Location postcode */
  postcode: string;
  /** Location time zone id (not used) */
  timeZoneId?: string | undefined;
  /** Club id which location belongLocation */
  clubId: number;
  /** Location type */
  type: LocationTypes;
}

export enum LocationTypes {
  Unknown = 0,
  TrainingGround1 = 2,
  Offsite = 3,
  League = 4,
  ContentSessionRoom = 5,
  TrainingGround2 = 6,
  TrainingGround3 = 7,
  TrainingGround = 8,
}

/** Request information */
export class RequestDetailsDto implements IRequestDetailsDto {
  /** Request id */
  id: string;
  /** Request participants */
  participants?: ParticipantDto[] | undefined;
  /** Broadcaster name */
  broadcaster?: string | undefined;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request status */
  status: RequestStatus;
  /** Id of creator */
  createdBy?: string | undefined;
  /** Name of creator */
  createdByName?: string | undefined;
  /** Date of creation */
  createOn: moment.Moment;
  /** UserModel id who made last update */
  updatedBy?: string | undefined;
  /** UserModel name who made last update */
  updatedByName?: string | undefined;
  /** Date of last update */
  updatedOn: moment.Moment;
  /** Broadcaster note info */
  broadcasterNote?: NoteDto | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Request company id (Broadcaster id) */
  companyId?: string | undefined;

  constructor(data?: IRequestDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.broadcaster = _data["broadcaster"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      this.status = _data["status"];
      this.createdBy = _data["createdBy"];
      this.createdByName = _data["createdByName"];
      this.createOn = _data["createOn"]
        ? moment.parseZone(_data["createOn"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedByName = _data["updatedByName"];
      this.updatedOn = _data["updatedOn"]
        ? moment.parseZone(_data["updatedOn"].toString())
        : <any>undefined;
      this.broadcasterNote = _data["broadcasterNote"]
        ? NoteDto.fromJS(_data["broadcasterNote"])
        : <any>undefined;
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.companyId = _data["companyId"];
    }
  }

  static fromJS(data: any): RequestDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new RequestDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["broadcaster"] = this.broadcaster;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    data["status"] = this.status;
    data["createdBy"] = this.createdBy;
    data["createdByName"] = this.createdByName;
    data["createOn"] = this.createOn
      ? this.createOn.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedByName"] = this.updatedByName;
    data["updatedOn"] = this.updatedOn
      ? this.updatedOn.toISOString(true)
      : <any>undefined;
    data["broadcasterNote"] = this.broadcasterNote
      ? this.broadcasterNote.toJSON()
      : <any>undefined;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["companyId"] = this.companyId;
    return data;
  }
}

/** Request information */
export interface IRequestDetailsDto {
  /** Request id */
  id: string;
  /** Request participants */
  participants?: ParticipantDto[] | undefined;
  /** Broadcaster name */
  broadcaster?: string | undefined;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request status */
  status: RequestStatus;
  /** Id of creator */
  createdBy?: string | undefined;
  /** Name of creator */
  createdByName?: string | undefined;
  /** Date of creation */
  createOn: moment.Moment;
  /** UserModel id who made last update */
  updatedBy?: string | undefined;
  /** UserModel name who made last update */
  updatedByName?: string | undefined;
  /** Date of last update */
  updatedOn: moment.Moment;
  /** Broadcaster note info */
  broadcasterNote?: NoteDto | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Request company id (Broadcaster id) */
  companyId?: string | undefined;
}

/** Participant model */
export class ParticipantDto implements IParticipantDto {
  /** Participant id */
  id: string;
  /** Participant name */
  name?: string | undefined;
  /** Participant phone */
  phone?: string | undefined;
  /** Participant email */
  email?: string | undefined;
  /** Participant type */
  type: ParticipantType;

  constructor(data?: IParticipantDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.name = _data["name"];
      this.phone = _data["phone"];
      this.email = _data["email"];
      this.type = _data["type"];
    }
  }

  static fromJS(data: any): ParticipantDto {
    data = typeof data === "object" ? data : {};
    let result = new ParticipantDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["name"] = this.name;
    data["phone"] = this.phone;
    data["email"] = this.email;
    data["type"] = this.type;
    return data;
  }
}

/** Participant model */
export interface IParticipantDto {
  /** Participant id */
  id: string;
  /** Participant name */
  name?: string | undefined;
  /** Participant phone */
  phone?: string | undefined;
  /** Participant email */
  email?: string | undefined;
  /** Participant type */
  type: ParticipantType;
}

export enum ParticipantType {
  Unknow = 0,
  Reporter = 1,
  Camera = 2,
  Producer = 3,
  Other = 4,
}

export enum CameraOperator {
  PLPCamera = 0,
  BroadcasterCamera = 1,
  AudioOnly = 2,
}

export enum RequestStatus {
  Unknown = 0,
  Draft = 1,
  Submitted = 2,
  Approved = 3,
  Reject = 4,
  Withdrawn = 5,
}

export class CreateAdditionalContentFixtureDto implements ICreateAdditionalContentFixtureDto {
  type: AdditionalContentType;
  arrival: moment.Moment;
  start: moment.Moment;
  locationId: string;
  languages?: LanguageDto[] | undefined;
  clubNote?: NoteDto | undefined;
  peoples?: PeopleDto[] | undefined;
  status: EventStatus;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  clubId?: number;
  leagueNote?: NoteDto | undefined;

  constructor(data?: ICreateAdditionalContentFixtureDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.type = _data["type"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.locationId = _data["locationId"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PeopleDto.fromJS(item));
      }
      this.status = _data["status"];
      this.participants = _data["participants"];
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      this.clubId = _data["clubId"];
      this.leagueNote = _data["leagueNote"];
    }
  }

  static fromJS(data: any): CreateAdditionalContentFixtureDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateAdditionalContentFixtureDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["type"] = this.type;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["locationId"] = this.locationId;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    data["status"] = this.status;
    data["participants"] = this.participants;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    data["clubId"] = this.clubId;
    data["leagueNote"] = this.leagueNote;
    return data;
  }
}

export interface ICreateAdditionalContentFixtureDto {
  type: AdditionalContentType;
  arrival: moment.Moment;
  start: moment.Moment;
  locationId: string;
  languages?: LanguageDto[] | undefined;
  clubNote?: NoteDto | undefined;
  peoples?: PeopleDto[] | undefined;
  status: EventStatus;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  clubId?: number;
}

export class UpdateAdditionalContentFixtureDto implements IUpdateAdditionalContentFixtureDto {
  eventId: string;
  type: AdditionalContentType;
  status: EventStatus;
  arrival: moment.Moment;
  start: moment.Moment;
  locationId: string;
  languages?: LanguageDto[] | undefined;
  clubNote?: NoteDto | undefined;
  leagueNote?: NoteDto | undefined;
  peoples?: PeopleDto[] | undefined;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  clubId?: number | undefined;

  constructor(data?: IUpdateAdditionalContentFixtureDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.eventId = _data["eventId"];
      this.type = _data["type"];
      this.status = _data["status"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.locationId = _data["locationId"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PeopleDto.fromJS(item));
      }
      this.participants = _data["participants"];
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      this.clubId = _data["clubId"];
    }
  }

  static fromJS(data: any): UpdateAdditionalContentFixtureDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateAdditionalContentFixtureDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["eventId"] = this.eventId;
    data["type"] = this.type;
    data["status"] = this.status;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["locationId"] = this.locationId;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    data["participants"] = this.participants;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    data["clubId"] = this.clubId;
    return data;
  }
}

export interface IUpdateAdditionalContentFixtureDto {
  eventId: string;
  type: AdditionalContentType;
  status: EventStatus;
  arrival: moment.Moment;
  start: moment.Moment;
  locationId: string;
  languages?: LanguageDto[] | undefined;
  clubNote?: NoteDto | undefined;
  leagueNote?: NoteDto | undefined;
  peoples?: PeopleDto[] | undefined;
  participants?: string | undefined;
  duration?: moment.Duration | undefined;
  clubId?: number | undefined;
}

export class CreateParticipantDto implements ICreateParticipantDto {
  /** Participant name */
  name?: string | undefined;
  /** Participant phone */
  phone: string;
  /** Participant email */
  email: string;
  /** Participant type */
  type: ParticipantType;

  constructor(data?: ICreateParticipantDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data["name"];
      this.phone = _data["phone"];
      this.email = _data["email"];
      this.type = _data["type"];
    }
  }

  static fromJS(data: any): CreateParticipantDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateParticipantDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["name"] = this.name;
    data["phone"] = this.phone;
    data["email"] = this.email;
    data["type"] = this.type;
    return data;
  }
}

export interface ICreateParticipantDto {
  /** Participant name */
  name?: string | undefined;
  /** Participant phone */
  phone: string;
  /** Participant email */
  email: string;
  /** Participant type */
  type: ParticipantType;
}

export interface IClubContentSessionRequestDto {
  requestId: string;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;
}

export class CreateAdditionalContentSessionRequestDto implements ICreateAdditionalContentSessionRequestDto {
  eventId: string;
  status: RequestStatus;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;
  note?: string | undefined;
  additionalContentType: AdditionalContentType;

  constructor(data?: ICreateAdditionalContentSessionRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.eventId = _data["eventId"];
      this.status = _data["status"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.note = _data["note"];
      this.additionalContentType = _data["additionalContentType"];
    }
  }

  static fromJS(data: any): CreateAdditionalContentSessionRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateAdditionalContentSessionRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["eventId"] = this.eventId;
    data["status"] = this.status;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["note"] = this.note;
    data["additionalContentType"] = this.additionalContentType;
    return data;
  }
}

export interface ICreateAdditionalContentSessionRequestDto {
  eventId: string;
  status: RequestStatus;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;
  note?: string | undefined;
  additionalContentType: AdditionalContentType;
}

export class UpdateAdditionalContentSessionRequestDto implements IUpdateAdditionalContentSessionRequestDto {
  id: string;
  eventId: string;
  status: RequestStatus;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;
  clubNote?: NoteDto | undefined;
  leagueNote?: NoteDto | undefined;
  broadcasterNote?: NoteDto | undefined;
  additionalContentType: AdditionalContentType;

  constructor(data?: IUpdateAdditionalContentSessionRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.eventId = _data["eventId"];
      this.status = _data["status"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.broadcasterNote = _data["broadcasterNote"]
        ? NoteDto.fromJS(_data["broadcasterNote"])
        : <any>undefined;
      this.additionalContentType = _data["additionalContentType"];
    }
  }

  static fromJS(data: any): UpdateAdditionalContentSessionRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateAdditionalContentSessionRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["eventId"] = this.eventId;
    data["status"] = this.status;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["broadcasterNote"] = this.broadcasterNote
      ? this.broadcasterNote.toJSON()
      : <any>undefined;
    data["additionalContentType"] = this.additionalContentType;
    return data;
  }
}

export interface IUpdateAdditionalContentSessionRequestDto {
  id: string;
  eventId: string;
  status: RequestStatus;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;
  clubNote?: NoteDto | undefined;
  leagueNote?: NoteDto | undefined;
  broadcasterNote?: NoteDto | undefined;
  additionalContentType: AdditionalContentType;
}

/** Club model */
export class ClubDto implements IClubDto {
  /** Club Id */
  id: number;
  /** Club Name */
  name?: string | undefined;

  constructor(data?: IClubDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.name = _data["name"];
    }
  }

  static fromJS(data: any): ClubDto {
    data = typeof data === "object" ? data : {};
    let result = new ClubDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["name"] = this.name;
    return data;
  }
}

/** Club model */
export interface IClubDto {
  /** Club Id */
  id: number;
  /** Club Name */
  name?: string | undefined;
}

export class ContentSessionTypeDto implements IContentSessionTypeDto {
  id: number;
  name?: string | undefined;

  constructor(data?: IContentSessionTypeDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.name = _data["name"];
    }
  }

  static fromJS(data: any): ContentSessionTypeDto {
    data = typeof data === "object" ? data : {};
    let result = new ContentSessionTypeDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["name"] = this.name;
    return data;
  }
}

export interface IContentSessionTypeDto {
  id: number;
  name?: string | undefined;
}

export class FilmingLocationInfoDto implements IFilmingLocationInfoDto {
  matchesInfo?: MatchFilmingLocationInfoDto[] | undefined;
  locations?: FilmingLocationDto[] | undefined;

  constructor(data?: IFilmingLocationInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["matchesInfo"])) {
        this.matchesInfo = [] as any;
        for (let item of _data["matchesInfo"])
          this.matchesInfo.push(MatchFilmingLocationInfoDto.fromJS(item));
      }
      if (Array.isArray(_data["locations"])) {
        this.locations = [] as any;
        for (let item of _data["locations"])
          this.locations.push(FilmingLocationDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): FilmingLocationInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new FilmingLocationInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.matchesInfo)) {
      data["matchesInfo"] = [];
      for (let item of this.matchesInfo)
        data["matchesInfo"].push(item.toJSON());
    }
    if (Array.isArray(this.locations)) {
      data["locations"] = [];
      for (let item of this.locations) data["locations"].push(item.toJSON());
    }
    return data;
  }
}

export interface IFilmingLocationInfoDto {
  matchesInfo?: MatchFilmingLocationInfoDto[] | undefined;
  locations?: FilmingLocationDto[] | undefined;
}

export class MatchFilmingLocationInfoDto implements IMatchFilmingLocationInfoDto {
  matchInfo?: MatchDto | undefined;
  matchFilmingLocation?: ResultOfMatchFilmingLocationDetailsDto | undefined;

  constructor(data?: IMatchFilmingLocationInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchInfo = _data["matchInfo"]
        ? MatchDto.fromJS(_data["matchInfo"])
        : <any>undefined;
      this.matchFilmingLocation = _data["matchFilmingLocation"]
        ? ResultOfMatchFilmingLocationDetailsDto.fromJS(
            _data["matchFilmingLocation"],
          )
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchFilmingLocationInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchFilmingLocationInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchInfo"] = this.matchInfo
      ? this.matchInfo.toJSON()
      : <any>undefined;
    data["matchFilmingLocation"] = this.matchFilmingLocation
      ? this.matchFilmingLocation.toJSON()
      : <any>undefined;
    return data;
  }
}

export interface IMatchFilmingLocationInfoDto {
  matchInfo?: MatchDto | undefined;
  matchFilmingLocation?: ResultOfMatchFilmingLocationDetailsDto | undefined;
}

/** Match model */
export class MatchDto implements IMatchDto {
  /** Match id */
  matchId: number;
  /** Match day (round number) */
  matchDay: number;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  /** Match Date */
  date: moment.Moment;
  /** Away team event info */
  awayTeamEvent?: FixtureEventDto | undefined;
  /** Home team event info */
  homeTeamEvent?: FixtureEventDto | undefined;

  constructor(data?: IMatchDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.matchDay = _data["matchDay"];
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
      this.date = _data["date"]
        ? moment.parseZone(_data["date"].toString())
        : <any>undefined;
      this.awayTeamEvent = _data["awayTeamEvent"]
        ? FixtureEventDto.fromJS(_data["awayTeamEvent"])
        : <any>undefined;
      this.homeTeamEvent = _data["homeTeamEvent"]
        ? FixtureEventDto.fromJS(_data["homeTeamEvent"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["matchDay"] = this.matchDay;
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    data["date"] = this.date ? this.date.toISOString(true) : <any>undefined;
    data["awayTeamEvent"] = this.awayTeamEvent
      ? this.awayTeamEvent.toJSON()
      : <any>undefined;
    data["homeTeamEvent"] = this.homeTeamEvent
      ? this.homeTeamEvent.toJSON()
      : <any>undefined;
    return data;
  }
}

/** Match model */
export interface IMatchDto {
  /** Match id */
  matchId: number;
  /** Match day (round number) */
  matchDay: number;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  /** Match Date */
  date: moment.Moment;
  /** Away team event info */
  awayTeamEvent?: FixtureEventDto | undefined;
  /** Home team event info */
  homeTeamEvent?: FixtureEventDto | undefined;
}

/** Team info model */
export class TeamDto implements ITeamDto {
  /** Team id (fdf) */
  id: number;
  /** Team code (fdf) */
  code?: string | undefined;
  /** Team official name */
  officialName?: string | undefined;
  /** Team name */
  name?: string | undefined;

  constructor(data?: ITeamDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.code = _data["code"];
      this.officialName = _data["officialName"];
      this.name = _data["name"];
    }
  }

  static fromJS(data: any): TeamDto {
    data = typeof data === "object" ? data : {};
    let result = new TeamDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["code"] = this.code;
    data["officialName"] = this.officialName;
    data["name"] = this.name;
    return data;
  }
}

/** Team info model */
export interface ITeamDto {
  /** Team id (fdf) */
  id: number;
  /** Team code (fdf) */
  code?: string | undefined;
  /** Team official name */
  officialName?: string | undefined;
  /** Team name */
  name?: string | undefined;
}

export class FixtureEventDto implements IFixtureEventDto {
  /** Match id */
  matchId: number;
  /** Fixture event id */
  fixtureEventId: string;
  /** Event date */
  date: moment.Moment;
  /** Peoples that participate in event */
  peoples?: PlayerDto[] | undefined;
  /** Count of requests for event (submitted/approved) */
  requestsCount: number;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Created by person info */
  createdBy?: string | undefined;
  /** Created by person info */
  createdByName?: string | undefined;
  /** Created date */
  createOnUtc: moment.Moment;
  /** Updated by person info */
  updatedBy?: string | undefined;
  /** Updated by person info */
  updatedByName?: string | undefined;
  /** Updated date */
  updatedOnUtc: moment.Moment;
  /** Location info */
  location?: LocationDto | undefined;
  /** Club id which event belong to */
  clubId: number;
  /** List of languages for event */
  languages?: LanguageDto[] | undefined;
  /** Requests details for event */
  requestDetails?: RequestDetailsDto[] | undefined;
  /** Total count of request */
  requestTotal?: RequestTotal | undefined;

  constructor(data?: IFixtureEventDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.fixtureEventId = _data["fixtureEventId"];
      this.date = _data["date"]
        ? moment.parseZone(_data["date"].toString())
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PlayerDto.fromJS(item));
      }
      this.requestsCount = _data["requestsCount"];
      this.status = _data["status"];
      this.type = _data["type"];
      this.createdBy = _data["createdBy"];
      this.createdByName = _data["createdByName"];
      this.createOnUtc = _data["createOnUtc"]
        ? moment.parseZone(_data["createOnUtc"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedByName = _data["updatedByName"];
      this.updatedOnUtc = _data["updatedOnUtc"]
        ? moment.parseZone(_data["updatedOnUtc"].toString())
        : <any>undefined;
      this.location = _data["location"]
        ? LocationDto.fromJS(_data["location"])
        : <any>undefined;
      this.clubId = _data["clubId"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["requestDetails"])) {
        this.requestDetails = [] as any;
        for (let item of _data["requestDetails"])
          this.requestDetails.push(RequestDetailsDto.fromJS(item));
      }
      this.requestTotal = _data["requestTotal"]
        ? RequestTotal.fromJS(_data["requestTotal"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): FixtureEventDto {
    data = typeof data === "object" ? data : {};
    let result = new FixtureEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["fixtureEventId"] = this.fixtureEventId;
    data["date"] = this.date ? this.date.toISOString(true) : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    data["requestsCount"] = this.requestsCount;
    data["status"] = this.status;
    data["type"] = this.type;
    data["createdBy"] = this.createdBy;
    data["createdByName"] = this.createdByName;
    data["createOnUtc"] = this.createOnUtc
      ? this.createOnUtc.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedByName"] = this.updatedByName;
    data["updatedOnUtc"] = this.updatedOnUtc
      ? this.updatedOnUtc.toISOString(true)
      : <any>undefined;
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    data["clubId"] = this.clubId;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    if (Array.isArray(this.requestDetails)) {
      data["requestDetails"] = [];
      for (let item of this.requestDetails)
        data["requestDetails"].push(item.toJSON());
    }
    data["requestTotal"] = this.requestTotal
      ? this.requestTotal.toJSON()
      : <any>undefined;
    return data;
  }
}

export interface IFixtureEventDto {
  /** Match id */
  matchId: number;
  /** Fixture event id */
  fixtureEventId: string;
  /** Event date */
  date: moment.Moment;
  /** Peoples that participate in event */
  peoples?: PlayerDto[] | undefined;
  /** Count of requests for event (submitted/approved) */
  requestsCount: number;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Created by person info */
  createdBy?: string | undefined;
  /** Created by person info */
  createdByName?: string | undefined;
  /** Created date */
  createOnUtc: moment.Moment;
  /** Updated by person info */
  updatedBy?: string | undefined;
  /** Updated by person info */
  updatedByName?: string | undefined;
  /** Updated date */
  updatedOnUtc: moment.Moment;
  /** Location info */
  location?: LocationDto | undefined;
  /** Club id which event belong to */
  clubId: number;
  /** List of languages for event */
  languages?: LanguageDto[] | undefined;
  /** Requests details for event */
  requestDetails?: RequestDetailsDto[] | undefined;
  /** Total count of request */
  requestTotal?: RequestTotal | undefined;
}

/** Person which participate in event */
export class PlayerDto implements IPlayerDto {
  /** Person id */
  id: number;
  /** Person official name */
  officialName?: string | undefined;
  /** Person official surname */
  officialSurname?: string | undefined;
  /** Person short name */
  shortName?: string | undefined;
  /** Person bpp count */
  bppCount: number;
  /** Person appearances count */
  appCount: number;
  /** Person role (player/manager) */
  role?: string | undefined;
  bios?: string | undefined;

  constructor(data?: IPlayerDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.officialName = _data["officialName"];
      this.officialSurname = _data["officialSurname"];
      this.shortName = _data["shortName"];
      this.bppCount = _data["bppCount"];
      this.appCount = _data["appCount"];
      this.role = _data["role"];
      this.bios = _data["bios"];
    }
  }

  static fromJS(data: any): PlayerDto {
    data = typeof data === "object" ? data : {};
    let result = new PlayerDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["officialName"] = this.officialName;
    data["officialSurname"] = this.officialSurname;
    data["shortName"] = this.shortName;
    data["bppCount"] = this.bppCount;
    data["appCount"] = this.appCount;
    data["role"] = this.role;
    data["bios"] = this.bios;
    return data;
  }
}

/** Person which participate in event */
export interface IPlayerDto {
  /** Person id */
  id: number;
  /** Person official name */
  officialName?: string | undefined;
  /** Person official surname */
  officialSurname?: string | undefined;
  /** Person short name */
  shortName?: string | undefined;
  /** Person bpp count */
  bppCount: number;
  /** Person appearances count */
  appCount: number;
  /** Person role (player/manager) */
  role?: string | undefined;
  bios?: string | undefined;
}

export enum FixtureEventType {
  Standard = 0,
  Double = 1,
  Extended = 3,
  DoubleExtended = 4,
  ExtendedBroadcasterExclusive = 5,
  ExtendedMasterclass = 6,
}

/** Requests count info */
export class RequestTotal implements IRequestTotal {
  /** Requested requests count */
  requested: number;
  /** rejected requests count */
  rejected: number;
  /** Approved requests count */
  approved: number;

  constructor(data?: IRequestTotal) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.requested = _data["requested"];
      this.rejected = _data["rejected"];
      this.approved = _data["approved"];
    }
  }

  static fromJS(data: any): RequestTotal {
    data = typeof data === "object" ? data : {};
    let result = new RequestTotal();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["requested"] = this.requested;
    data["rejected"] = this.rejected;
    data["approved"] = this.approved;
    return data;
  }
}

/** Requests count info */
export interface IRequestTotal {
  /** Requested requests count */
  requested: number;
  /** rejected requests count */
  rejected: number;
  /** Approved requests count */
  approved: number;
}

export class ResultOfMatchFilmingLocationDetailsDto implements IResultOfMatchFilmingLocationDetailsDto {
  isSuccess: boolean;
  value?: MatchFilmingLocationDetailsDto | undefined;

  constructor(data?: IResultOfMatchFilmingLocationDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isSuccess = _data["isSuccess"];
      this.value = _data["value"]
        ? MatchFilmingLocationDetailsDto.fromJS(_data["value"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): ResultOfMatchFilmingLocationDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new ResultOfMatchFilmingLocationDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["isSuccess"] = this.isSuccess;
    data["value"] = this.value ? this.value.toJSON() : <any>undefined;
    return data;
  }
}

export interface IResultOfMatchFilmingLocationDetailsDto {
  isSuccess: boolean;
  value?: MatchFilmingLocationDetailsDto | undefined;
}

export class MatchFilmingLocationDetailsDto implements IMatchFilmingLocationDetailsDto {
  matchId: number;
  note?: string | undefined;
  locations?: MatchFilmingLocationDetailsItemDto[] | undefined;

  constructor(data?: IMatchFilmingLocationDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.note = _data["note"];
      if (Array.isArray(_data["locations"])) {
        this.locations = [] as any;
        for (let item of _data["locations"])
          this.locations.push(MatchFilmingLocationDetailsItemDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): MatchFilmingLocationDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchFilmingLocationDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["note"] = this.note;
    if (Array.isArray(this.locations)) {
      data["locations"] = [];
      for (let item of this.locations) data["locations"].push(item.toJSON());
    }
    return data;
  }
}

export interface IMatchFilmingLocationDetailsDto {
  matchId: number;
  note?: string | undefined;
  locations?: MatchFilmingLocationDetailsItemDto[] | undefined;
}

export class MatchFilmingLocationDetailsItemDto implements IMatchFilmingLocationDetailsItemDto {
  locationId: number;
  startTime: moment.Moment;
  endTime: moment.Moment;

  constructor(data?: IMatchFilmingLocationDetailsItemDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.locationId = _data["locationId"];
      this.startTime = _data["startTime"]
        ? moment.parseZone(_data["startTime"].toString())
        : <any>undefined;
      this.endTime = _data["endTime"]
        ? moment.parseZone(_data["endTime"].toString())
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchFilmingLocationDetailsItemDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchFilmingLocationDetailsItemDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["locationId"] = this.locationId;
    data["startTime"] = this.startTime
      ? this.startTime.toISOString(true)
      : <any>undefined;
    data["endTime"] = this.endTime
      ? this.endTime.toISOString(true)
      : <any>undefined;
    return data;
  }
}

export interface IMatchFilmingLocationDetailsItemDto {
  locationId: number;
  startTime: moment.Moment;
  endTime: moment.Moment;
}

export class FilmingLocationDto implements IFilmingLocationDto {
  clubId: number;
  locationId: number;
  locationName?: string | undefined;

  constructor(data?: IFilmingLocationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.clubId = _data["clubId"];
      this.locationId = _data["locationId"];
      this.locationName = _data["locationName"];
    }
  }

  static fromJS(data: any): FilmingLocationDto {
    data = typeof data === "object" ? data : {};
    let result = new FilmingLocationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["clubId"] = this.clubId;
    data["locationId"] = this.locationId;
    data["locationName"] = this.locationName;
    return data;
  }
}

export interface IFilmingLocationDto {
  clubId: number;
  locationId: number;
  locationName?: string | undefined;
}

export class MatchFilmingLocationsDto implements IMatchFilmingLocationsDto {
  items?: MatchFilmingLocationOffsetItemDto[] | undefined;
  note?: string | undefined;

  constructor(data?: IMatchFilmingLocationsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(MatchFilmingLocationOffsetItemDto.fromJS(item));
      }
      this.note = _data["note"];
    }
  }

  static fromJS(data: any): MatchFilmingLocationsDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchFilmingLocationsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["note"] = this.note;
    return data;
  }
}

export interface IMatchFilmingLocationsDto {
  items?: MatchFilmingLocationOffsetItemDto[] | undefined;
  note?: string | undefined;
}

export class MatchFilmingLocationOffsetItemDto implements IMatchFilmingLocationOffsetItemDto {
  locationId: number;
  startHours: number;
  startMinutes: number;
  endHours: number;
  endMinutes: number;

  constructor(data?: IMatchFilmingLocationOffsetItemDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.locationId = _data["locationId"];
      this.startHours = _data["startHours"];
      this.startMinutes = _data["startMinutes"];
      this.endHours = _data["endHours"];
      this.endMinutes = _data["endMinutes"];
    }
  }

  static fromJS(data: any): MatchFilmingLocationOffsetItemDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchFilmingLocationOffsetItemDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["locationId"] = this.locationId;
    data["startHours"] = this.startHours;
    data["startMinutes"] = this.startMinutes;
    data["endHours"] = this.endHours;
    data["endMinutes"] = this.endMinutes;
    return data;
  }
}

export interface IMatchFilmingLocationOffsetItemDto {
  locationId: number;
  startHours: number;
  startMinutes: number;
  endHours: number;
  endMinutes: number;
}

/** Fixture + Match info model */
export class MatchFixtureEventDto
  extends FixtureEventDto
  implements IMatchFixtureEventDto
{
  /** Match day */
  matchDay: number;
  /** Match date */
  matchDate: moment.Moment;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;

  constructor(data?: IMatchFixtureEventDto) {
    super(data);
  }

  init(_data?: any) {
    super.init(_data);
    if (_data) {
      this.matchDay = _data["matchDay"];
      this.matchDate = _data["matchDate"]
        ? moment.parseZone(_data["matchDate"].toString())
        : <any>undefined;
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchFixtureEventDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchFixtureEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchDay"] = this.matchDay;
    data["matchDate"] = this.matchDate
      ? this.matchDate.toISOString(true)
      : <any>undefined;
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    super.toJSON(data);
    return data;
  }
}

/** Fixture + Match info model */
export interface IMatchFixtureEventDto extends IFixtureEventDto {
  /** Match day */
  matchDay: number;
  /** Match date */
  matchDate: moment.Moment;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
}

/** Week (round) matches model */
export class WeekDto implements IWeekDto {
  /** Week number (round number) */
  weekNumber: number;
  /** Matches list */
  matches?: MatchDto[] | undefined;

  constructor(data?: IWeekDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.weekNumber = _data["weekNumber"];
      if (Array.isArray(_data["matches"])) {
        this.matches = [] as any;
        for (let item of _data["matches"])
          this.matches.push(MatchDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): WeekDto {
    data = typeof data === "object" ? data : {};
    let result = new WeekDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["weekNumber"] = this.weekNumber;
    if (Array.isArray(this.matches)) {
      data["matches"] = [];
      for (let item of this.matches) data["matches"].push(item.toJSON());
    }
    return data;
  }
}

/** Week (round) matches model */
export interface IWeekDto {
  /** Week number (round number) */
  weekNumber: number;
  /** Matches list */
  matches?: MatchDto[] | undefined;
}

/** Fixture event details model */
export class FixtureEventDetailsDto implements IFixtureEventDetailsDto {
  /** Match id */
  matchId: number;
  /** Match date */
  matchDate: moment.Moment;
  /** Event id */
  eventId: string;
  /** Peoples info */
  peoples?: PlayerListDto | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Admin note info */
  adminLeagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Season which event and match belongs to */
  seasonId: string;
  /** Event arrival time */
  arrival: moment.Moment;
  /** Event start time */
  start: moment.Moment;
  /** Event location info */
  location?: LocationDto | undefined;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  /** UserModel info who created event */
  createdBy?: string | undefined;
  /** UserModel info who created event */
  createdByName?: string | undefined;
  /** Created time */
  createOn: moment.Moment;
  /** UserModel info who updated event */
  updatedBy?: string | undefined;
  /** UserModel info who updated event */
  updatedByName?: string | undefined;
  /** Updated time */
  updatedOn: moment.Moment;
  /** Event languages info */
  languages?: LanguageDto[] | undefined;
  /** Event requests info */
  requestDetails?: RequestDetailsDto[] | undefined;
  /** Event club id (dublicate) */
  clubId: number;
  /** Event club name */
  club?: string | undefined;
  /** Event club id () */
  companyId: number;
  /** REqesut total info */
  requestTotal?: RequestTotal | undefined;
  /** Flag indicates that event was approved previously */
  wasApproved: boolean;
  isNonLiveInUK: boolean;

  constructor(data?: IFixtureEventDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.matchDate = _data["matchDate"]
        ? moment.parseZone(_data["matchDate"].toString())
        : <any>undefined;
      this.eventId = _data["eventId"];
      this.peoples = _data["peoples"]
        ? PlayerListDto.fromJS(_data["peoples"])
        : <any>undefined;
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.adminLeagueNote = _data["adminLeagueNote"]
        ? NoteDto.fromJS(_data["adminLeagueNote"])
        : <any>undefined;
      this.status = _data["status"];
      this.type = _data["type"];
      this.seasonId = _data["seasonId"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.location = _data["location"]
        ? LocationDto.fromJS(_data["location"])
        : <any>undefined;
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
      this.createdBy = _data["createdBy"];
      this.createdByName = _data["createdByName"];
      this.createOn = _data["createOn"]
        ? moment.parseZone(_data["createOn"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedByName = _data["updatedByName"];
      this.updatedOn = _data["updatedOn"]
        ? moment.parseZone(_data["updatedOn"].toString())
        : <any>undefined;
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["requests"])) {
        this.requestDetails = [] as any;
        for (let item of _data["requests"])
          this.requestDetails.push(RequestDetailsDto.fromJS(item));
      }
      this.clubId = _data["clubId"];
      this.club = _data["club"];
      this.companyId = _data["companyId"];
      this.requestTotal = _data["requestTotal"]
        ? RequestTotal.fromJS(_data["requestTotal"])
        : <any>undefined;
      this.wasApproved = _data["wasApproved"];
      this.isNonLiveInUK = _data["isNonLiveInUK"];
    }
  }

  static fromJS(data: any): FixtureEventDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new FixtureEventDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["matchDate"] = this.matchDate
      ? this.matchDate.toISOString(true)
      : <any>undefined;
    data["eventId"] = this.eventId;
    data["peoples"] = this.peoples ? this.peoples.toJSON() : <any>undefined;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["adminLeagueNote"] = this.adminLeagueNote
      ? this.adminLeagueNote.toJSON()
      : <any>undefined;
    data["status"] = this.status;
    data["type"] = this.type;
    data["seasonId"] = this.seasonId;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    data["createdBy"] = this.createdBy;
    data["createdByName"] = this.createdByName;
    data["createOn"] = this.createOn
      ? this.createOn.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedByName"] = this.updatedByName;
    data["updatedOn"] = this.updatedOn
      ? this.updatedOn.toISOString(true)
      : <any>undefined;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    if (Array.isArray(this.requestDetails)) {
      data["requestDetails"] = [];
      for (let item of this.requestDetails)
        data["requestDetails"].push(item.toJSON());
    }
    data["clubId"] = this.clubId;
    data["club"] = this.club;
    data["companyId"] = this.companyId;
    data["requestTotal"] = this.requestTotal
      ? this.requestTotal.toJSON()
      : <any>undefined;
    data["wasApproved"] = this.wasApproved;
    data["isNonLiveInUK"] = this.isNonLiveInUK;
    return data;
  }
}

/** Fixture event details model */
export interface IFixtureEventDetailsDto {
  /** Match id */
  matchId: number;
  /** Match date */
  matchDate: moment.Moment;
  /** Event id */
  eventId: string;
  /** Peoples info */
  peoples?: PlayerListDto | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Admin note info */
  adminLeagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Season which event and match belongs to */
  seasonId: string;
  /** Event arrival time */
  arrival: moment.Moment;
  /** Event start time */
  start: moment.Moment;
  /** Event location info */
  location?: LocationDto | undefined;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  /** UserModel info who created event */
  createdBy?: string | undefined;
  /** UserModel info who created event */
  createdByName?: string | undefined;
  /** Created time */
  createOn: moment.Moment;
  /** UserModel info who updated event */
  updatedBy?: string | undefined;
  /** UserModel info who updated event */
  updatedByName?: string | undefined;
  /** Updated time */
  updatedOn: moment.Moment;
  /** Event languages info */
  languages?: LanguageDto[] | undefined;
  /** Event requests info */
  requestDetails?: RequestDetailsDto[] | undefined;
  /** Event club id (dublicate) */
  clubId: number;
  /** Event club name */
  club?: string | undefined;
  /** Event club id () */
  companyId: number;
  /** REqesut total info */
  requestTotal?: RequestTotal | undefined;
  /** Flag indicates that event was approved previously */
  wasApproved: boolean;
  isNonLiveInUK: boolean;
}

export class PlayerListDto implements IPlayerListDto {
  team?: TeamDto | undefined;
  players?: PlayerDto[] | undefined;

  constructor(data?: IPlayerListDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.team = _data["team"]
        ? TeamDto.fromJS(_data["team"])
        : <any>undefined;
      if (Array.isArray(_data["players"])) {
        this.players = [] as any;
        for (let item of _data["players"])
          this.players.push(PlayerDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): PlayerListDto {
    data = typeof data === "object" ? data : {};
    let result = new PlayerListDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["team"] = this.team ? this.team.toJSON() : <any>undefined;
    if (Array.isArray(this.players)) {
      data["players"] = [];
      for (let item of this.players) data["players"].push(item.toJSON());
    }
    return data;
  }
}

export interface IPlayerListDto {
  team?: TeamDto | undefined;
  players?: PlayerDto[] | undefined;
}

/** Create fixture event model */
export class CreateFixtureEventDto implements ICreateFixtureEventDto {
  /** Match id */
  matchId: number;
  /** Arrival date and time */
  arrival: moment.Moment;
  /** Start date and time */
  start: moment.Moment;
  /** Event persons */
  peoples?: PeopleDto[] | undefined;
  /** Location id */
  locationId: string;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Event languages info list */
  languages?: LanguageDto[] | undefined;
  isNonLiveInUK: boolean;

  constructor(data?: ICreateFixtureEventDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PeopleDto.fromJS(item));
      }
      this.locationId = _data["locationId"];
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.status = _data["status"];
      this.type = _data["type"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      this.isNonLiveInUK = _data["isNonLiveInUK"];
    }
  }

  static fromJS(data: any): CreateFixtureEventDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateFixtureEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    data["locationId"] = this.locationId;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["status"] = this.status;
    data["type"] = this.type;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    data["isNonLiveInUK"] = this.isNonLiveInUK;
    return data;
  }
}

/** Create fixture event model */
export interface ICreateFixtureEventDto {
  /** Match id */
  matchId: number;
  /** Arrival date and time */
  arrival: moment.Moment;
  /** Start date and time */
  start: moment.Moment;
  /** Event persons */
  peoples?: PeopleDto[] | undefined;
  /** Location id */
  locationId: string;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Event languages info list */
  languages?: LanguageDto[] | undefined;
  isNonLiveInUK: boolean;
}

/** Update fixture event model */
export class UpdateFixtureEventDto implements IUpdateFixtureEventDto {
  /** Update fixture event model */
  id: string;
  /** Update fixture event model */
  matchId: number;
  /** Event arrival date and time */
  arrival: moment.Moment;
  /** Event start date and time */
  start: moment.Moment;
  /** Peoples assigned to event */
  peoples: PeopleDto[];
  /** Id of location for event */
  locationId: string;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Admin note info */
  adminLeagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Languages info models */
  languages?: LanguageDto[] | undefined;
  isNonLiveInUK: boolean;

  constructor(data?: IUpdateFixtureEventDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
    if (!data) {
      this.peoples = [];
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.matchId = _data["matchId"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PeopleDto.fromJS(item));
      }
      this.locationId = _data["locationId"];
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.adminLeagueNote = _data["adminLeagueNote"]
        ? NoteDto.fromJS(_data["adminLeagueNote"])
        : <any>undefined;
      this.status = _data["status"];
      this.type = _data["type"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      this.isNonLiveInUK = _data["isNonLiveInUK"];
    }
  }

  static fromJS(data: any): UpdateFixtureEventDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateFixtureEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["matchId"] = this.matchId;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    data["locationId"] = this.locationId;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["adminLeagueNote"] = this.adminLeagueNote
      ? this.adminLeagueNote.toJSON()
      : <any>undefined;
    data["status"] = this.status;
    data["type"] = this.type;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    data["isNonLiveInUK"] = this.isNonLiveInUK;
    return data;
  }
}

/** Update fixture event model */
export interface IUpdateFixtureEventDto {
  /** Update fixture event model */
  id: string;
  /** Update fixture event model */
  matchId: number;
  /** Event arrival date and time */
  arrival: moment.Moment;
  /** Event start date and time */
  start: moment.Moment;
  /** Peoples assigned to event */
  peoples: PeopleDto[];
  /** Id of location for event */
  locationId: string;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Admin note info */
  adminLeagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event type */
  type: FixtureEventType;
  /** Languages info models */
  languages?: LanguageDto[] | undefined;
  isNonLiveInUK: boolean;
}

/** Match info model */
export class MatchInfoDto implements IMatchInfoDto {
  /** Match id */
  matchId: number;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  /** Match dte  */
  date: moment.Moment;

  constructor(data?: IMatchInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
      this.date = _data["date"]
        ? moment.parseZone(_data["date"].toString())
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    data["date"] = this.date ? this.date.toISOString(true) : <any>undefined;
    return data;
  }
}

/** Match info model */
export interface IMatchInfoDto {
  /** Match id */
  matchId: number;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  /** Match dte  */
  date: moment.Moment;
}

/** Create fixture request model */
export class CreateFixtureRequestDto implements ICreateFixtureRequestDto {
  /** fixture event id */
  fixtureEventId: string;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** List of attached persons */
  participants?: ParticipantDto[] | undefined;
  /** Note for request */
  note?: string | undefined;

  constructor(data?: ICreateFixtureRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.fixtureEventId = _data["fixtureEventId"];
      this.status = _data["status"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.note = _data["note"];
    }
  }

  static fromJS(data: any): CreateFixtureRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateFixtureRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["fixtureEventId"] = this.fixtureEventId;
    data["status"] = this.status;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["note"] = this.note;
    return data;
  }
}

/** Create fixture request model */
export interface ICreateFixtureRequestDto {
  /** fixture event id */
  fixtureEventId: string;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** List of attached persons */
  participants?: ParticipantDto[] | undefined;
  /** Note for request */
  note?: string | undefined;
}

export class UpdateFixtureRequestDto implements IUpdateFixtureRequestDto {
  /** Request id */
  fixtureRequestId: string;
  /** Fixture event id */
  fixtureEventId: string;
  /** Broadcaster note text */
  note?: string | undefined;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request participants */
  participants?: ParticipantDto[] | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note info */
  broadcasterNote?: NoteDto | undefined;

  constructor(data?: IUpdateFixtureRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.fixtureRequestId = _data["fixtureRequestId"];
      this.fixtureEventId = _data["fixtureEventId"];
      this.note = _data["note"];
      this.status = _data["status"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.broadcasterNote = _data["broadcasterNote"]
        ? NoteDto.fromJS(_data["broadcasterNote"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): UpdateFixtureRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateFixtureRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["fixtureRequestId"] = this.fixtureRequestId;
    data["fixtureEventId"] = this.fixtureEventId;
    data["note"] = this.note;
    data["status"] = this.status;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["broadcasterNote"] = this.broadcasterNote
      ? this.broadcasterNote.toJSON()
      : <any>undefined;
    return data;
  }
}

export interface IUpdateFixtureRequestDto {
  /** Request id */
  fixtureRequestId: string;
  /** Fixture event id */
  fixtureEventId: string;
  /** Broadcaster note text */
  note?: string | undefined;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request participants */
  participants?: ParticipantDto[] | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note info */
  broadcasterNote?: NoteDto | undefined;
}

/** Import operation result */
export class ImportResultDto implements IImportResultDto {
  /** Flag indicates success of import operation */
  isImported: boolean;
  /** List of messages for import operation */
  messages?: string[] | undefined;

  constructor(data?: IImportResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isImported = _data["isImported"];
      if (Array.isArray(_data["messages"])) {
        this.messages = [] as any;
        for (let item of _data["messages"]) this.messages.push(item);
      }
    }
  }

  static fromJS(data: any): ImportResultDto {
    data = typeof data === "object" ? data : {};
    let result = new ImportResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["isImported"] = this.isImported;
    if (Array.isArray(this.messages)) {
      data["messages"] = [];
      for (let item of this.messages) data["messages"].push(item);
    }
    return data;
  }
}

/** Import operation result */
export interface IImportResultDto {
  /** Flag indicates success of import operation */
  isImported: boolean;
  /** List of messages for import operation */
  messages?: string[] | undefined;
}

export interface IInterviewDto {
  /** Event id */
  interviewId: string;
  /** Interview type */
  type?: InterviewTypeDto | undefined;
  /** Arrival date */
  arrival: moment.Moment;
  /** Start date */
  start: moment.Moment;
  /** Interviewee */
  interviewee?: string | undefined;
  /** Location */
  locationId: string;
  /** Location title for broadcaster */
  locationTitle?: string | undefined;
  status: EventStatus;
  requestStatus: RequestStatus;
  totalRequests: number;
  managedRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
}

export interface IInterviewTypeDto {
  interviewTypeId: string;
  interviewTypeName?: string | undefined;
  description?: string | undefined;
}

export interface IResultOfInterviewDetailsDto {
  isSuccess: boolean;
  value?: InterviewDetailsDto | undefined;
}

/** Interview details */
export interface IInterviewDetailsDto {
  /** Event id */
  interviewId: string;
  /** Interview type name */
  interviewType?: InterviewTypeDto | undefined;
  /** Arrival date */
  arrival: moment.Moment;
  /** Start date */
  start: moment.Moment;
  /** Interviewee */
  interviewee?: string | undefined;
  /** Location */
  locationId: string;
  /** Location title for broadcaster */
  locationTitle?: string | undefined;
  /** League notes */
  leagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event languages */
  interviewLanguages?: InterviewsLanguageDto[] | undefined;
  /** Event requests */
  requests?: InterviewsRequestDto[] | undefined;
  /** Requests statistic */
  requestsInfo?: InterviewsRequestsInfoDto | undefined;
  /** Created by  */
  createdBy?: string | undefined;
  /** Created on Utc */
  createdOnUtc: moment.Moment;
  /** Updated by */
  updatedBy?: string | undefined;
  /** Updated by utc */
  updatedOnUtc: moment.Moment;
}

export interface IInterviewsLanguageDto {
  languageId: string;
}

export interface ISealedObject {}

/** Interview request */
export interface IInterviewsRequestDto extends ISealedObject {
  /** If of the entity */
  interviewRequestId: string;
  /** Request participants */
  requestParticipant?: ParticipantDto[] | undefined;
  /** Broadcaster Id */
  broadcaster?: string | undefined;
  /** Company Id */
  companyId?: string | undefined;
  /** Is Cameras Enabled */
  isCamerasEnabled: boolean;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request status */
  status: RequestStatus;
  /** League note */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note */
  broadcasterNote?: NoteDto | undefined;
  /** Id of creator */
  createdBy?: string | undefined;
  /** Date of creation */
  createOnUtc: moment.Moment;
  /** UserModel id who made last update */
  updatedBy?: string | undefined;
  /** Date of last update */
  updatedOnUtc: moment.Moment;
  wasApproved: boolean;
}

export interface IInterviewsRequestsInfoDto {
  approved: number;
  rejected: number;
  requested: number;
}

export class ValidationResult implements IValidationResult {
  messages?: ValidationMessge[] | undefined;
  success: boolean;

  constructor(data?: IValidationResult) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["messages"])) {
        this.messages = [] as any;
        for (let item of _data["messages"])
          this.messages.push(ValidationMessge.fromJS(item));
      }
      this.success = _data["success"];
    }
  }

  static fromJS(data: any): ValidationResult {
    data = typeof data === "object" ? data : {};
    let result = new ValidationResult();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.messages)) {
      data["messages"] = [];
      for (let item of this.messages) data["messages"].push(item.toJSON());
    }
    data["success"] = this.success;
    return data;
  }
}

export interface IValidationResult {
  messages?: ValidationMessge[] | undefined;
  success: boolean;
}

export interface IValidationResultOfInterviewsRequestDto extends IValidationResult {
  value?: InterviewsRequestDto | undefined;
}

export interface IValidationMessge {
  message?: string | undefined;
}

export interface ICreateInterviewsRequestDto extends IInterviewsRequestDto {
  /** Interview id */
  interviewId: string;
}

/** model to create additional language (not used) */
export class CreateLanguageDto implements ICreateLanguageDto {
  /** Language name */
  name: string;

  constructor(data?: ICreateLanguageDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.name = _data["name"];
    }
  }

  static fromJS(data: any): CreateLanguageDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateLanguageDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["name"] = this.name;
    return data;
  }
}

/** model to create additional language (not used) */
export interface ICreateLanguageDto {
  /** Language name */
  name: string;
}

export class LocationDetailsDto implements ILocationDetailsDto {
  locationId: string;
  /** Location title */
  title: string;
  /** Location address string 1 */
  address1: string;
  /** Location address string 2 */
  address2?: string | undefined;
  /** Location city/town */
  townCity: string;
  /** Location country */
  country: string;
  /** Location postcode */
  postcode: string;
  /** Club id which location belong */
  clubId: number;
  /** Location timezone id (not used) */
  timeZoneId?: string | undefined;
  /** Location type */
  type: LocationTypes;

  constructor(data?: ILocationDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.locationId = _data["locationId"];
      this.title = _data["title"];
      this.address1 = _data["address1"];
      this.address2 = _data["address2"];
      this.townCity = _data["townCity"];
      this.country = _data["country"];
      this.postcode = _data["postcode"];
      this.clubId = _data["clubId"];
      this.timeZoneId = _data["timeZoneId"];
      this.type = _data["type"];
    }
  }

  static fromJS(data: any): LocationDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new LocationDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["locationId"] = this.locationId;
    data["title"] = this.title;
    data["address1"] = this.address1;
    data["address2"] = this.address2;
    data["townCity"] = this.townCity;
    data["country"] = this.country;
    data["postcode"] = this.postcode;
    data["clubId"] = this.clubId;
    data["timeZoneId"] = this.timeZoneId;
    data["type"] = this.type;
    return data;
  }
}

export interface ILocationDetailsDto {
  locationId: string;
  /** Location title */
  title: string;
  /** Location address string 1 */
  address1: string;
  /** Location address string 2 */
  address2?: string | undefined;
  /** Location city/town */
  townCity: string;
  /** Location country */
  country: string;
  /** Location postcode */
  postcode: string;
  /** Club id which location belong */
  clubId: number;
  /** Location timezone id (not used) */
  timeZoneId?: string | undefined;
  /** Location type */
  type: LocationTypes;
}

/** Location creation model */
export class CreateLocationDto implements ICreateLocationDto {
  /** Location title */
  title: string;
  /** Location address string 1 */
  address1: string;
  /** Location address string 2 */
  address2?: string | undefined;
  /** Location city/town */
  townCity: string;
  /** Location country */
  country: string;
  /** Location postcode */
  postcode: string;
  /** Club id which location belong */
  clubId: number;
  /** True if location belong to league */
  leagueType: boolean;
  /** Location type for the new Location */
  type: number;

  constructor(data?: ICreateLocationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.title = _data["title"];
      this.address1 = _data["address1"];
      this.address2 = _data["address2"];
      this.townCity = _data["townCity"];
      this.country = _data["country"];
      this.postcode = _data["postcode"];
      this.clubId = _data["clubId"];
      this.leagueType = _data["leagueType"];
      this.type = _data["type"];
    }
  }

  static fromJS(data: any): CreateLocationDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateLocationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["title"] = this.title;
    data["address1"] = this.address1;
    data["address2"] = this.address2;
    data["townCity"] = this.townCity;
    data["country"] = this.country;
    data["postcode"] = this.postcode;
    data["clubId"] = this.clubId;
    data["leagueType"] = this.leagueType;
    data["type"] = this.type;
    return data;
  }
}

/** Location creation model */
export interface ICreateLocationDto {
  /** Location title */
  title: string;
  /** Location address string 1 */
  address1: string;
  /** Location address string 2 */
  address2?: string | undefined;
  /** Location city/town */
  townCity: string;
  /** Location country */
  country: string;
  /** Location postcode */
  postcode: string;
  /** Club id which location belong */
  clubId: number;
  /** True if location belong to league */
  leagueType: boolean;
  /** Location type for the new Location */
  type: number;
}

export class LocationTypeDto implements ILocationTypeDto {
  id: number;
  name?: string | undefined;
  maxQuantityPerClub: number;
  isLeagueExclusive: boolean;

  constructor(data?: ILocationTypeDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.name = _data["name"];
      this.maxQuantityPerClub = _data["maxQuantityPerClub"];
      this.isLeagueExclusive = _data["isLeagueExclusive"];
    }
  }

  static fromJS(data: any): LocationTypeDto {
    data = typeof data === "object" ? data : {};
    let result = new LocationTypeDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["name"] = this.name;
    data["maxQuantityPerClub"] = this.maxQuantityPerClub;
    data["isLeagueExclusive"] = this.isLeagueExclusive;
    return data;
  }
}

export interface ILocationTypeDto {
  id: number;
  name?: string | undefined;
  maxQuantityPerClub: number;
  isLeagueExclusive: boolean;
}

export class MatchExtInfoDto implements IMatchExtInfoDto {
  matchId: number;
  awayTeamCode?: string | undefined;
  awayTeamId: number;
  awayTeamName?: string | undefined;
  homeTeamCode?: string | undefined;
  homeTeamId: number;
  homeTeamName?: string | undefined;
  date: moment.Moment;
  nonLiveInUK: boolean;
  matchDay: number;

  constructor(data?: IMatchExtInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.awayTeamCode = _data["awayTeamCode"];
      this.awayTeamId = _data["awayTeamId"];
      this.awayTeamName = _data["awayTeamName"];
      this.homeTeamCode = _data["homeTeamCode"];
      this.homeTeamId = _data["homeTeamId"];
      this.homeTeamName = _data["homeTeamName"];
      this.date = _data["date"]
        ? moment.parseZone(_data["date"].toString())
        : <any>undefined;
      this.nonLiveInUK = _data["nonLiveInUK"];
      this.matchDay = _data["matchDay"];
    }
  }

  static fromJS(data: any): MatchExtInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchExtInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["awayTeamCode"] = this.awayTeamCode;
    data["awayTeamId"] = this.awayTeamId;
    data["awayTeamName"] = this.awayTeamName;
    data["homeTeamCode"] = this.homeTeamCode;
    data["homeTeamId"] = this.homeTeamId;
    data["homeTeamName"] = this.homeTeamName;
    data["date"] = this.date ? this.date.toISOString(true) : <any>undefined;
    data["nonLiveInUK"] = this.nonLiveInUK;
    data["matchDay"] = this.matchDay;
    return data;
  }
}

export interface IMatchExtInfoDto {
  matchId: number;
  awayTeamCode?: string | undefined;
  awayTeamId: number;
  awayTeamName?: string | undefined;
  homeTeamCode?: string | undefined;
  homeTeamId: number;
  homeTeamName?: string | undefined;
  date: moment.Moment;
  nonLiveInUK: boolean;
  matchDay: number;
}

export class ResultOfMatchDetailsDto implements IResultOfMatchDetailsDto {
  isSuccess: boolean;
  value?: MatchDetailsDto | undefined;

  constructor(data?: IResultOfMatchDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isSuccess = _data["isSuccess"];
      this.value = _data["value"]
        ? MatchDetailsDto.fromJS(_data["value"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): ResultOfMatchDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new ResultOfMatchDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["isSuccess"] = this.isSuccess;
    data["value"] = this.value ? this.value.toJSON() : <any>undefined;
    return data;
  }
}

export interface IResultOfMatchDetailsDto {
  isSuccess: boolean;
  value?: MatchDetailsDto | undefined;
}

export class MatchDetailsDto implements IMatchDetailsDto {
  matchId: number;
  nonLiveInUK: boolean;

  constructor(data?: IMatchDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.nonLiveInUK = _data["nonLiveInUK"];
    }
  }

  static fromJS(data: any): MatchDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["nonLiveInUK"] = this.nonLiveInUK;
    return data;
  }
}

export interface IMatchDetailsDto {
  matchId: number;
  nonLiveInUK: boolean;
}

export interface IMatchRoundNumbersDto {
  matchRoundNumbers: number[];
  currentMatchRoundNumber: number;
}

export class MatchRoundNumbersDto implements IMatchRoundNumbersDto {
  matchRoundNumbers: number[];
  currentMatchRoundNumber: number;

  constructor(data?: IMatchDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchRoundNumbers = _data["matchRoundNumbers"];
      this.currentMatchRoundNumber = _data["currentMatchRoundNumber"];
    }
  }

  static fromJS(data: any): MatchRoundNumbersDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchRoundNumbersDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchRoundNumbers"] = this.matchRoundNumbers;
    data["currentMatchRoundNumber"] = this.currentMatchRoundNumber;
    return data;
  }
}

/** Save note information */
export class SaveNoteDto implements ISaveNoteDto {
  /** Note status (for admin notes) */
  status: NoteStatus;
  /** Note text */
  message: string;
  /** Not used */
  objectId: string;

  constructor(data?: ISaveNoteDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.status = _data["status"];
      this.message = _data["message"];
      this.objectId = _data["objectId"];
    }
  }

  static fromJS(data: any): SaveNoteDto {
    data = typeof data === "object" ? data : {};
    let result = new SaveNoteDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["status"] = this.status;
    data["message"] = this.message;
    data["objectId"] = this.objectId;
    return data;
  }
}

/** Save note information */
export interface ISaveNoteDto {
  /** Note status (for admin notes) */
  status: NoteStatus;
  /** Note text */
  message: string;
  /** Not used */
  objectId: string;
}

/** Person report model */
export class PersonReportDataDto implements IPersonReportDataDto {
  /** Club id which person belong */
  clubId: number;
  /** Club name which person belong */
  clubName?: string | undefined;
  /** Person full name */
  fullName?: string | undefined;
  /** Person last name */
  lastName?: string | undefined;
  /** Person first name */
  firstName?: string | undefined;
  /** Person bpp count */
  bppCount: number;
  /** Person appearances count */
  appCount: number;
  /** Person role (player/manager) */
  role: MemberTypeDto;

  constructor(data?: IPersonReportDataDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.clubId = _data["clubId"];
      this.clubName = _data["clubName"];
      this.fullName = _data["fullName"];
      this.lastName = _data["lastName"];
      this.firstName = _data["firstName"];
      this.bppCount = _data["bppCount"];
      this.appCount = _data["appCount"];
      this.role = _data["role"];
    }
  }

  static fromJS(data: any): PersonReportDataDto {
    data = typeof data === "object" ? data : {};
    let result = new PersonReportDataDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["clubId"] = this.clubId;
    data["clubName"] = this.clubName;
    data["fullName"] = this.fullName;
    data["lastName"] = this.lastName;
    data["firstName"] = this.firstName;
    data["bppCount"] = this.bppCount;
    data["appCount"] = this.appCount;
    data["role"] = this.role;
    return data;
  }
}

/** Person report model */
export interface IPersonReportDataDto {
  /** Club id which person belong */
  clubId: number;
  /** Club name which person belong */
  clubName?: string | undefined;
  /** Person full name */
  fullName?: string | undefined;
  /** Person last name */
  lastName?: string | undefined;
  /** Person first name */
  firstName?: string | undefined;
  /** Person bpp count */
  bppCount: number;
  /** Person appearances count */
  appCount: number;
  /** Person role (player/manager) */
  role: MemberTypeDto;
}

/** Content Session Type model */
export class ContentSessionTypeDataDto implements IContentSessionTypeDataDto {
  /** Club id */
  clubId: number;
  /** Club name */
  clubName?: string | undefined;

  data: Record<string, number>;

  constructor(data?: IContentSessionTypeDataDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.clubId = _data["clubId"];
      this.clubName = _data["clubName"];
      this.data = _data["data"];
    }
  }

  static fromJS(data: any): ContentSessionTypeDataDto {
    data = typeof data === "object" ? data : {};
    let result = new ContentSessionTypeDataDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["clubId"] = this.clubId;
    data["clubName"] = this.clubName;
    data["data"] = this.data;
    return data;
  }
}

/** Content Session Type model */
export interface IContentSessionTypeDataDto {
  /** Club id */
  clubId: number;
  /** Club name */
  clubName?: string | undefined;

  data: Record<string, number>;
  /** Stadard count */
}

export class InterviewsSummaryDataDto implements IInterviewsSummaryDataDto {
  club?: string | undefined;
  contentSessionRoom: number;
  offSite: number;
  trainingGround1: number;
  trainingGround2: number;
  trainingGround3: number;
  trainingGround: number;
  icsNonLive: number;
  preSeason: number;
  midSeason: number;
  clubContent: number;
  trainingGroundBehindScenes: number;
  trainings: number;

  constructor(data?: IInterviewsSummaryDataDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.club = _data["club"];
      this.contentSessionRoom = _data["contentSessionRoom"];
      this.offSite = _data["offSite"];
      this.trainingGround1 = _data["trainingGround1"];
      this.trainingGround2 = _data["trainingGround2"];
      this.trainingGround3 = _data["trainingGround3"];
      this.trainingGround = _data["trainingGround"];
      this.icsNonLive = _data["icsNonLive"];
      this.preSeason = _data["preSeason"];
      this.midSeason = _data["midSeason"];
      this.clubContent = _data["clubContent"];
      this.trainingGroundBehindScenes = _data["trainingGroundBehindScenes"];
      this.trainings = _data["trainings"];
    }
  }

  static fromJS(data: any): InterviewsSummaryDataDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewsSummaryDataDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["club"] = this.club;
    data["contentSessionRoom"] = this.contentSessionRoom;
    data["offSite"] = this.offSite;
    data["trainingGround1"] = this.trainingGround1;
    data["trainingGround2"] = this.trainingGround2;
    data["trainingGround3"] = this.trainingGround3;
    data["trainingGround"] = this.trainingGround;
    data["icsNonLive"] = this.icsNonLive;
    data["preSeason"] = this.preSeason;
    data["midSeason"] = this.midSeason;
    data["clubContent"] = this.clubContent;
    data["trainingGroundBehindScenes"] = this.trainingGroundBehindScenes;
    data["trainings"] = this.trainings;
    return data;
  }
}

export interface IInterviewsSummaryDataDto {
  club?: string | undefined;
  contentSessionRoom: number;
  offSite: number;
  trainingGround1: number;
  trainingGround2: number;
  trainingGround3: number;
  trainingGround: number;
  icsNonLive: number;
  preSeason: number;
  midSeason: number;
  clubContent: number;
  trainingGroundBehindScenes: number;
  trainings: number;
}

export class BroadcasterRequestsDataDto implements IBroadcasterRequestsDataDto {
  broadcaster?: string | undefined;
  exclusive: number;
  icsLive?: RequestsInfoDto | undefined;
  icsNonLive?: RequestsInfoDto | undefined;
  clubContent?: RequestsInfoDto | undefined;
  trainings?: RequestsInfoDto | undefined;
  plInterview?: RequestsInfoDto | undefined;

  constructor(data?: IBroadcasterRequestsDataDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.broadcaster = _data["broadcaster"];
      this.exclusive = _data["exclusive"];
      this.icsLive = _data["icsLive"]
        ? RequestsInfoDto.fromJS(_data["icsLive"])
        : <any>undefined;
      this.icsNonLive = _data["icsNonLive"]
        ? RequestsInfoDto.fromJS(_data["icsNonLive"])
        : <any>undefined;
      this.clubContent = _data["clubContent"]
        ? RequestsInfoDto.fromJS(_data["clubContent"])
        : <any>undefined;
      this.trainings = _data["trainings"]
        ? RequestsInfoDto.fromJS(_data["trainings"])
        : <any>undefined;
      this.plInterview = _data["plInterview"]
        ? RequestsInfoDto.fromJS(_data["plInterview"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): BroadcasterRequestsDataDto {
    data = typeof data === "object" ? data : {};
    let result = new BroadcasterRequestsDataDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["broadcaster"] = this.broadcaster;
    data["exclusive"] = this.exclusive;
    data["icsLive"] = this.icsLive ? this.icsLive.toJSON() : <any>undefined;
    data["icsNonLive"] = this.icsNonLive
      ? this.icsNonLive.toJSON()
      : <any>undefined;
    data["clubContent"] = this.clubContent
      ? this.clubContent.toJSON()
      : <any>undefined;
    data["trainings"] = this.trainings
      ? this.trainings.toJSON()
      : <any>undefined;
    data["plInterview"] = this.plInterview
      ? this.plInterview.toJSON()
      : <any>undefined;
    return data;
  }
}

export interface IBroadcasterRequestsDataDto {
  broadcaster?: string | undefined;
  exclusive: number;
  icsLive?: RequestsInfoDto | undefined;
  icsNonLive?: RequestsInfoDto | undefined;
  clubContent?: RequestsInfoDto | undefined;
  trainings?: RequestsInfoDto | undefined;
  plInterview?: RequestsInfoDto | undefined;
}

export class RequestsInfoDto implements IRequestsInfoDto {
  approved: number;
  rejected: number;

  constructor(data?: IRequestsInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.approved = _data["approved"];
      this.rejected = _data["rejected"];
    }
  }

  static fromJS(data: any): RequestsInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new RequestsInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["approved"] = this.approved;
    data["rejected"] = this.rejected;
    return data;
  }
}

export interface IRequestsInfoDto {
  approved: number;
  rejected: number;
}

/** Common paged list */
export class PagedListOfTeamMemberDto implements IPagedListOfTeamMemberDto {
  /** Page data */
  data?: TeamMemberDto[] | undefined;
  pageSize: number;
  pageNumber: number;
  /** Total count of elements */
  totalCount: number;

  constructor(data?: IPagedListOfTeamMemberDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["data"])) {
        this.data = [] as any;
        for (let item of _data["data"])
          this.data.push(TeamMemberDto.fromJS(item));
      }
      this.pageSize = _data["pageSize"];
      this.pageNumber = _data["pageNumber"];
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): PagedListOfTeamMemberDto {
    data = typeof data === "object" ? data : {};
    let result = new PagedListOfTeamMemberDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.data)) {
      data["data"] = [];
      for (let item of this.data) data["data"].push(item.toJSON());
    }
    data["pageSize"] = this.pageSize;
    data["pageNumber"] = this.pageNumber;
    data["totalCount"] = this.totalCount;
    return data;
  }
}

/** Common paged list */
export interface IPagedListOfTeamMemberDto {
  /** Page data */
  data?: TeamMemberDto[] | undefined;
  pageSize: number;
  pageNumber: number;
  /** Total count of elements */
  totalCount: number;
}

export class TeamMemberDto implements ITeamMemberDto {
  id: number;
  firstName?: string | undefined;
  lastName?: string | undefined;
  bios?: string | undefined;
  role?: string | undefined;

  constructor(data?: ITeamMemberDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.firstName = _data["firstName"];
      this.lastName = _data["lastName"];
      this.bios = _data["bios"];
      this.role = _data["role"];
    }
  }

  static fromJS(data: any): TeamMemberDto {
    data = typeof data === "object" ? data : {};
    let result = new TeamMemberDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["firstName"] = this.firstName;
    data["lastName"] = this.lastName;
    data["bios"] = this.bios;
    data["role"] = this.role;
    return data;
  }
}

export interface ITeamMemberDto {
  id: number;
  firstName?: string | undefined;
  lastName?: string | undefined;
  bios?: string | undefined;
  role?: string | undefined;
}

export class ResultOfTeamMemberDto implements IResultOfTeamMemberDto {
  isSuccess: boolean;
  value?: TeamMemberDto | undefined;

  constructor(data?: IResultOfTeamMemberDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isSuccess = _data["isSuccess"];
      this.value = _data["value"]
        ? TeamMemberDto.fromJS(_data["value"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): ResultOfTeamMemberDto {
    data = typeof data === "object" ? data : {};
    let result = new ResultOfTeamMemberDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["isSuccess"] = this.isSuccess;
    data["value"] = this.value ? this.value.toJSON() : <any>undefined;
    return data;
  }
}

export interface IResultOfTeamMemberDto {
  isSuccess: boolean;
  value?: TeamMemberDto | undefined;
}

export class UpdateTeamMemberDto implements IUpdateTeamMemberDto {
  id: number;
  bios?: string | undefined;

  constructor(data?: IUpdateTeamMemberDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.bios = _data["bios"];
    }
  }

  static fromJS(data: any): UpdateTeamMemberDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateTeamMemberDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["bios"] = this.bios;
    return data;
  }
}

export interface IUpdateTeamMemberDto {
  id: number;
  bios?: string | undefined;
}

/** Common training info */
export class TrainingDto implements ITrainingDto {
  /** Training id */
  id?: string | undefined;
  /** Club name */
  club?: string | undefined;
  /** Club id */
  clubId: number;
  /** Training date */
  date: moment.Moment;
  /** Training status */
  status: EventStatus;
  /** Training Location information */
  location?: string | undefined;
  /** Number of total requests [League] */
  totalRequests?: number | undefined;
  /** Number of managed requests [League] */
  managedRequests?: number | undefined;
  /** Number of approved requests [League] */
  approvedRequests?: number | undefined;
  /** Number of rejected requests [League] */
  rejectedRequests?: number | undefined;

  constructor(data?: ITrainingDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.club = _data["club"];
      this.clubId = _data["clubId"];
      this.date = _data["date"]
        ? moment.parseZone(_data["date"].toString())
        : <any>undefined;
      this.status = _data["status"];
      this.location = _data["location"];
      this.totalRequests = _data["totalRequests"];
      this.managedRequests = _data["managedRequests"];
      this.approvedRequests = _data["approvedRequests"];
      this.rejectedRequests = _data["rejectedRequests"];
    }
  }

  static fromJS(data: any): TrainingDto {
    data = typeof data === "object" ? data : {};
    let result = new TrainingDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["club"] = this.club;
    data["clubId"] = this.clubId;
    data["date"] = this.date ? this.date.toISOString(true) : <any>undefined;
    data["status"] = this.status;
    data["location"] = this.location;
    data["totalRequests"] = this.totalRequests;
    data["managedRequests"] = this.managedRequests;
    data["approvedRequests"] = this.approvedRequests;
    data["rejectedRequests"] = this.rejectedRequests;
    return data;
  }
}

/** Common training info */
export interface ITrainingDto {
  /** Training id */
  id?: string | undefined;
  /** Club name */
  club?: string | undefined;
  /** Club id */
  clubId: number;
  /** Training date */
  date: moment.Moment;
  /** Training status */
  status: EventStatus;
  /** Training Location information */
  location?: string | undefined;
  /** Number of total requests [League] */
  totalRequests?: number | undefined;
  /** Number of managed requests [League] */
  managedRequests?: number | undefined;
  /** Number of approved requests [League] */
  approvedRequests?: number | undefined;
  /** Number of rejected requests [League] */
  rejectedRequests?: number | undefined;
}

/** Training detailed information */
export class TrainingDetailsDto implements ITrainingDetailsDto {
  /** Training id */
  id: string;
  /** Arrival time of event */
  arrival: moment.Moment;
  /** Start time of event */
  start: moment.Moment;
  /** Training status */
  status: EventStatus;
  /** Club note information */
  clubNote?: NoteDto | undefined;
  /** League note information */
  leagueNote?: NoteDto | undefined;
  /** Admin note information */
  adminLeagueNote?: NoteDto | undefined;
  /** Club name */
  club?: string | undefined;
  /** Club id */
  clubId: number;
  /** TRaining Location information */
  location?: LocationDto | undefined;
  /** Training Requests information */
  requests?: RequestDetailsDto[] | undefined;
  /** Flag indicates that training was approved before */
  wasApproved: boolean;
  /** Information about user who create training */
  createdBy?: string | undefined;
  /** Information about user who create training */
  createdByName?: string | undefined;
  /** Created date */
  createOn: moment.Moment;
  /** UserModel who made last update */
  updatedBy?: string | undefined;
  /** UserModel name who made last update */
  updatedByName?: string | undefined;
  /** Date of last update */
  updatedOn: moment.Moment;

  constructor(data?: ITrainingDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.status = _data["status"];
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.adminLeagueNote = _data["adminLeagueNote"]
        ? NoteDto.fromJS(_data["adminLeagueNote"])
        : <any>undefined;
      this.club = _data["club"];
      this.clubId = _data["clubId"];
      this.location = _data["location"]
        ? LocationDto.fromJS(_data["location"])
        : <any>undefined;
      if (Array.isArray(_data["requests"])) {
        this.requests = [] as any;
        for (let item of _data["requests"])
          this.requests.push(RequestDetailsDto.fromJS(item));
      }
      this.wasApproved = _data["wasApproved"];
      this.createdBy = _data["createdBy"];
      this.createdByName = _data["createdByName"];
      this.createOn = _data["createOn"]
        ? moment.parseZone(_data["createOn"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedByName = _data["updatedByName"];
      this.updatedOn = _data["updatedOn"]
        ? moment.parseZone(_data["updatedOn"].toString())
        : <any>undefined;
    }
  }

  static fromJS(data: any): TrainingDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new TrainingDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["status"] = this.status;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["adminLeagueNote"] = this.adminLeagueNote
      ? this.adminLeagueNote.toJSON()
      : <any>undefined;
    data["club"] = this.club;
    data["clubId"] = this.clubId;
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    if (Array.isArray(this.requests)) {
      data["requests"] = [];
      for (let item of this.requests) data["requests"].push(item.toJSON());
    }
    data["wasApproved"] = this.wasApproved;
    data["createdBy"] = this.createdBy;
    data["createdByName"] = this.createdByName;
    data["createOn"] = this.createOn
      ? this.createOn.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedByName"] = this.updatedByName;
    data["updatedOn"] = this.updatedOn
      ? this.updatedOn.toISOString(true)
      : <any>undefined;
    return data;
  }
}

/** Training detailed information */
export interface ITrainingDetailsDto {
  /** Training id */
  id: string;
  /** Arrival time of event */
  arrival: moment.Moment;
  /** Start time of event */
  start: moment.Moment;
  /** Training status */
  status: EventStatus;
  /** Club note information */
  clubNote?: NoteDto | undefined;
  /** League note information */
  leagueNote?: NoteDto | undefined;
  /** Admin note information */
  adminLeagueNote?: NoteDto | undefined;
  /** Club name */
  club?: string | undefined;
  /** Club id */
  clubId: number;
  /** TRaining Location information */
  location?: LocationDto | undefined;
  /** Training Requests information */
  requests?: RequestDetailsDto[] | undefined;
  /** Flag indicates that training was approved before */
  wasApproved: boolean;
  /** Information about user who create training */
  createdBy?: string | undefined;
  /** Information about user who create training */
  createdByName?: string | undefined;
  /** Created date */
  createOn: moment.Moment;
  /** UserModel who made last update */
  updatedBy?: string | undefined;
  /** UserModel name who made last update */
  updatedByName?: string | undefined;
  /** Date of last update */
  updatedOn: moment.Moment;
}

/** Create training model */
export class CreateTrainingDto implements ICreateTrainingDto {
  /** Arrival time of training event */
  arrival: moment.Moment;
  /** Start time of training event */
  start: moment.Moment;
  /** Location id that will used for training event */
  locationId: string;
  /** Status of training event */
  status: EventStatus;
  /** Note for training event */
  note?: CreateNoteDto | undefined;

  constructor(data?: ICreateTrainingDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.locationId = _data["locationId"];
      this.status = _data["status"];
      this.note = _data["note"]
        ? CreateNoteDto.fromJS(_data["note"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): CreateTrainingDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateTrainingDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["locationId"] = this.locationId;
    data["status"] = this.status;
    data["note"] = this.note ? this.note.toJSON() : <any>undefined;
    return data;
  }
}

/** Create training model */
export interface ICreateTrainingDto {
  /** Arrival time of training event */
  arrival: moment.Moment;
  /** Start time of training event */
  start: moment.Moment;
  /** Location id that will used for training event */
  locationId: string;
  /** Status of training event */
  status: EventStatus;
  /** Note for training event */
  note?: CreateNoteDto | undefined;
}

/** Create note model */
export class CreateNoteDto implements ICreateNoteDto {
  /** Note text */
  message?: string | undefined;

  constructor(data?: ICreateNoteDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.message = _data["message"];
    }
  }

  static fromJS(data: any): CreateNoteDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateNoteDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["message"] = this.message;
    return data;
  }
}

/** Create note model */
export interface ICreateNoteDto {
  /** Note text */
  message?: string | undefined;
}

/** Update training model */
export class UpdateTrainingDto implements IUpdateTrainingDto {
  /** Training id */
  id: string;
  /** arrival time */
  arrival: moment.Moment;
  /** Start time */
  start: moment.Moment;
  /** Id of location that uses for training */
  locationId: string;
  /** Training status */
  status: EventStatus;
  /** Club note information */
  clubNote?: UpdateNoteDto | undefined;
  /** League note information */
  leagueNote?: UpdateNoteDto | undefined;
  /** Admin league note information */
  adminLeagueNote?: UpdateNoteDto | undefined;
  /** list of training requests for training */
  requests?: UpdateTrainingRequestDto[] | undefined;

  constructor(data?: IUpdateTrainingDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.locationId = _data["locationId"];
      this.status = _data["status"];
      this.clubNote = _data["clubNote"]
        ? UpdateNoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? UpdateNoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.adminLeagueNote = _data["adminLeagueNote"]
        ? UpdateNoteDto.fromJS(_data["adminLeagueNote"])
        : <any>undefined;
      if (Array.isArray(_data["requests"])) {
        this.requests = [] as any;
        for (let item of _data["requests"])
          this.requests.push(UpdateTrainingRequestDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): UpdateTrainingDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateTrainingDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["locationId"] = this.locationId;
    data["status"] = this.status;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["adminLeagueNote"] = this.adminLeagueNote
      ? this.adminLeagueNote.toJSON()
      : <any>undefined;
    if (Array.isArray(this.requests)) {
      data["requests"] = [];
      for (let item of this.requests) data["requests"].push(item.toJSON());
    }
    return data;
  }
}

/** Update training model */
export interface IUpdateTrainingDto {
  /** Training id */
  id: string;
  /** arrival time */
  arrival: moment.Moment;
  /** Start time */
  start: moment.Moment;
  /** Id of location that uses for training */
  locationId: string;
  /** Training status */
  status: EventStatus;
  /** Club note information */
  clubNote?: UpdateNoteDto | undefined;
  /** League note information */
  leagueNote?: UpdateNoteDto | undefined;
  /** Admin league note information */
  adminLeagueNote?: UpdateNoteDto | undefined;
  /** list of training requests for training */
  requests?: UpdateTrainingRequestDto[] | undefined;
}

/** Update note data */
export class UpdateNoteDto implements IUpdateNoteDto {
  /** Text of note */
  message?: string | undefined;

  constructor(data?: IUpdateNoteDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.message = _data["message"];
    }
  }

  static fromJS(data: any): UpdateNoteDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateNoteDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["message"] = this.message;
    return data;
  }
}

/** Update note data */
export interface IUpdateNoteDto {
  /** Text of note */
  message?: string | undefined;
}

export class UpdateTrainingRequestDto implements IUpdateTrainingRequestDto {
  /** Request id */
  id: string;
  /** Training id which request belong */
  trainingId: string;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request participants */
  participants?: ParticipantDto[] | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note info */
  broadcasterNote?: NoteDto | undefined;

  constructor(data?: IUpdateTrainingRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.trainingId = _data["trainingId"];
      this.status = _data["status"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.broadcasterNote = _data["broadcasterNote"]
        ? NoteDto.fromJS(_data["broadcasterNote"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): UpdateTrainingRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new UpdateTrainingRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["trainingId"] = this.trainingId;
    data["status"] = this.status;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["broadcasterNote"] = this.broadcasterNote
      ? this.broadcasterNote.toJSON()
      : <any>undefined;
    return data;
  }
}

export interface IUpdateTrainingRequestDto {
  /** Request id */
  id: string;
  /** Training id which request belong */
  trainingId: string;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request participants */
  participants?: ParticipantDto[] | undefined;
  /** Club note info */
  clubNote?: NoteDto | undefined;
  /** League note info */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note info */
  broadcasterNote?: NoteDto | undefined;
}

/** Model to create new training request */
export class CreateTrainingRequestDto implements ICreateTrainingRequestDto {
  /** Training id */
  trainingId: string;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** List of attached persons */
  participants?: ParticipantDto[] | undefined;
  /** Note for request */
  note?: string | undefined;

  constructor(data?: ICreateTrainingRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.trainingId = _data["trainingId"];
      this.status = _data["status"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
      this.note = _data["note"];
    }
  }

  static fromJS(data: any): CreateTrainingRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateTrainingRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["trainingId"] = this.trainingId;
    data["status"] = this.status;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    data["note"] = this.note;
    return data;
  }
}

/** Model to create new training request */
export interface ICreateTrainingRequestDto {
  /** Training id */
  trainingId: string;
  /** Request status */
  status: RequestStatus;
  /** Camera operator */
  cameraOperator: CameraOperator;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** List of attached persons */
  participants?: ParticipantDto[] | undefined;
  /** Note for request */
  note?: string | undefined;
}

/** UserModel information */
export class UserDto implements IUserDto {
  /** Account name */
  account?: string | undefined;
  /** UserModel name */
  name?: string | undefined;
  /** List of user roles */
  roles?: string[] | undefined;
  /** Flag indicates that user was authenticated */
  authenticated: boolean;
  /** Redirect url after login */
  redirectUrl?: string | undefined;

  constructor(data?: IUserDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.account = _data["account"];
      this.name = _data["name"];
      if (Array.isArray(_data["roles"])) {
        this.roles = [] as any;
        for (let item of _data["roles"]) this.roles.push(item);
      }
      this.authenticated = _data["authenticated"];
      this.redirectUrl = _data["redirectUrl"];
    }
  }

  static fromJS(data: any): UserDto {
    data = typeof data === "object" ? data : {};
    let result = new UserDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["account"] = this.account;
    data["name"] = this.name;
    if (Array.isArray(this.roles)) {
      data["roles"] = [];
      for (let item of this.roles) data["roles"].push(item);
    }
    data["authenticated"] = this.authenticated;
    data["redirectUrl"] = this.redirectUrl;
    return data;
  }
}

/** UserModel information */
export interface IUserDto {
  /** Account name */
  account?: string | undefined;
  /** UserModel name */
  name?: string | undefined;
  /** List of user roles */
  roles?: string[] | undefined;
  /** Flag indicates that user was authenticated */
  authenticated: boolean;
  /** Redirect url after login */
  redirectUrl?: string | undefined;
}

/** Additional user details */
export class UserDetails implements IUserDetails {
  /** UserModel company name */
  company?: string | undefined;
  /** UserModel company id */
  id?: string | undefined;
  /** Applications list */
  applications?: ApplicationDto[] | undefined;
  /** Flag indicates that user details has been retrieved successfully  */
  success: boolean;
  /** Notification options for user */
  userNotifications?: UserNotificationType[] | undefined;

  constructor(data?: IUserDetails) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.company = _data["company"];
      this.id = _data["id"];
      if (Array.isArray(_data["applications"])) {
        this.applications = [] as any;
        for (let item of _data["applications"])
          this.applications.push(ApplicationDto.fromJS(item));
      }
      this.success = _data["success"];
      if (Array.isArray(_data["userNotifications"])) {
        this.userNotifications = [] as any;
        for (let item of _data["userNotifications"])
          this.userNotifications.push(UserNotificationType.fromJS(item));
      }
    }
  }

  static fromJS(data: any): UserDetails {
    data = typeof data === "object" ? data : {};
    let result = new UserDetails();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["company"] = this.company;
    data["id"] = this.id;
    if (Array.isArray(this.applications)) {
      data["applications"] = [];
      for (let item of this.applications)
        data["applications"].push(item.toJSON());
    }
    data["success"] = this.success;
    if (Array.isArray(this.userNotifications)) {
      data["userNotifications"] = [];
      for (let item of this.userNotifications)
        data["userNotifications"].push(item.toJSON());
    }
    return data;
  }
}

/** Additional user details */
export interface IUserDetails {
  /** UserModel company name */
  company?: string | undefined;
  /** UserModel company id */
  id?: string | undefined;
  /** Applications list */
  applications?: ApplicationDto[] | undefined;
  /** Flag indicates that user details has been retrieved successfully  */
  success: boolean;
  /** Notification options for user */
  userNotifications?: UserNotificationType[] | undefined;
}

export class ApplicationDto implements IApplicationDto {
  displayName?: string | undefined;
  homeUrl?: string | undefined;
  imageUrl?: string | undefined;
  isCurrent?: boolean;

  constructor(data?: IApplicationDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.displayName = _data["displayName"];
      this.homeUrl = _data["homeUrl"];
      this.imageUrl = _data["imageUrl"];
      this.isCurrent = _data["isCurrent"];
    }
  }

  static fromJS(data: any): ApplicationDto {
    data = typeof data === "object" ? data : {};
    let result = new ApplicationDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["displayName"] = this.displayName;
    data["homeUrl"] = this.homeUrl;
    data["imageUrl"] = this.imageUrl;
    data["isCurrent"] = this.isCurrent;
    return data;
  }
}

export interface IApplicationDto {
  displayName?: string | undefined;
  homeUrl?: string | undefined;
  imageUrl?: string | undefined;
  isCurrent?: boolean;
}

/** Notification types for user */
export class UserNotificationType implements IUserNotificationType {
  /** Notification type */
  notificationType: NotificationType;
  /** Flag indicates that notification can be selected/unselected for Dashboard */
  enabledForDashboard: boolean;
  /** Flag indicates that notification can be selected/unselected for Email */
  enabledForEmail: boolean;
  /** Flag indicates that notification selected for Dashboard (not used) (not used) */
  checkedForDashboard: boolean;
  /** Flag indicates that notification selected for Email */
  checkedForEmail: boolean;

  constructor(data?: IUserNotificationType) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.notificationType = _data["notificationType"];
      this.enabledForDashboard = _data["enabledForDashboard"];
      this.enabledForEmail = _data["enabledForEmail"];
      this.checkedForDashboard = _data["checkedForDashboard"];
      this.checkedForEmail = _data["checkedForEmail"];
    }
  }

  static fromJS(data: any): UserNotificationType {
    data = typeof data === "object" ? data : {};
    let result = new UserNotificationType();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["notificationType"] = this.notificationType;
    data["enabledForDashboard"] = this.enabledForDashboard;
    data["enabledForEmail"] = this.enabledForEmail;
    data["checkedForDashboard"] = this.checkedForDashboard;
    data["checkedForEmail"] = this.checkedForEmail;
    return data;
  }
}

/** Notification types for user */
export interface IUserNotificationType {
  /** Notification type */
  notificationType: NotificationType;
  /** Flag indicates that notification can be selected/unselected for Dashboard */
  enabledForDashboard: boolean;
  /** Flag indicates that notification can be selected/unselected for Email */
  enabledForEmail: boolean;
  /** Flag indicates that notification selected for Dashboard (not used) (not used) */
  checkedForDashboard: boolean;
  /** Flag indicates that notification selected for Email */
  checkedForEmail: boolean;
}

export enum NotificationType {
  Unknown = 0,
  ApproveTraining = 1,
  ApproveFixture = 2,
  RejectTraining = 3,
  RejectFixture = 4,
  TrainingSubmitted = 5,
  FixtureSubmitted = 6,
  ApproveTrainingRequest = 7,
  ApproveFixtureRequest = 8,
  RejectTrainingRequest = 9,
  RejectFixtureRequest = 10,
  SubmitTrainingRequest = 11,
  SubmitFixtureRequest = 12,
  RequestAwaitingInformation = 13,
  InterviewSubmitted = 14,
  InterviewRequestSubmitted = 15,
  InterviewRequestApproved = 16,
  InterviewRequestRejected = 17,
  FixtureRequestWithdrawn = 18,
  TrainingRequestWithdrawn = 19,
  FixtureExtendedICSApproved = 20,
  FixtureRequestExtendedICSSubmitted = 21,
  FixtureRequestExtendedICSApproved = 22,
  FixtureRequestExtendedICSRejected = 23,
  News = 24,
  AdditionalContentSubmitted = 25,
  AdditionalContentSubmittedByLeague = 26,
  AdditionalContentApproved = 27,
  AdditionalContentRejected = 28,
  AdditionalContentRequestSubmitted = 29,
  AdditionalContentRequestApproved = 30,
  AdditionalContentRequestRejected = 31,
}

export class MatchInterviewEventDto implements IMatchInterviewEventDto {
  matchId: number;
  matchDate: moment.Moment;
  homeTeam?: TeamDto | undefined;
  homeEvent?: InterviewEventDto | undefined;
  awayTeam?: TeamDto | undefined;
  awayEvent?: InterviewEventDto | undefined;

  constructor(data?: IMatchInterviewEventDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.matchDate = _data["matchDate"]
        ? moment.parseZone(_data["matchDate"].toString())
        : <any>undefined;
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.homeEvent = _data["homeEvent"]
        ? InterviewEventDto.fromJS(_data["homeEvent"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
      this.awayEvent = _data["awayEvent"]
        ? InterviewEventDto.fromJS(_data["awayEvent"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchInterviewEventDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchInterviewEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["matchDate"] = this.matchDate
      ? this.matchDate.toISOString(true)
      : <any>undefined;
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["homeEvent"] = this.homeEvent
      ? this.homeEvent.toJSON()
      : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    data["awayEvent"] = this.awayEvent
      ? this.awayEvent.toJSON()
      : <any>undefined;
    return data;
  }
}

export interface IMatchInterviewEventDto {
  matchId: number;
  matchDate: moment.Moment;
  homeTeam?: TeamDto | undefined;
  homeEvent?: InterviewEventDto | undefined;
  awayTeam?: TeamDto | undefined;
  awayEvent?: InterviewEventDto | undefined;
}

export class InterviewEventDto implements IInterviewEventDto {
  eventId: string;
  arrivalUtc: moment.Moment;
  peoples?: PlayerDto[] | undefined;
  languages?: LanguageDto[] | undefined;
  location?: LocationDto | undefined;
  requestTotal?: RequestTotal | undefined;
  requests?: RequestDto[] | undefined;
  status?: EventStatus | undefined;

  constructor(data?: IInterviewEventDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.eventId = _data["eventId"];
      this.arrivalUtc = _data["arrivalUtc"]
        ? moment.parseZone(_data["arrivalUtc"].toString())
        : <any>undefined;
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PlayerDto.fromJS(item));
      }
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["requests"])) {
        this.requests = [] as any;
        for (let item of _data["requests"])
          this.requests.push(RequestDto.fromJS(item));
      }
      this.location = _data["location"]
        ? LocationDto.fromJS(_data["location"])
        : <any>undefined;
      this.requestTotal = _data["requestTotal"]
        ? RequestTotal.fromJS(_data["requestTotal"])
        : <any>undefined;
      this.status = _data["status"];
    }
  }

  static fromJS(data: any): InterviewEventDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["eventId"] = this.eventId;
    data["arrivalUtc"] = this.arrivalUtc
      ? this.arrivalUtc.toISOString(true)
      : <any>undefined;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    if (Array.isArray(this.requests)) {
      data["requests"] = [];
      for (let item of this.requests) data["requests"].push(item.toJSON());
    }
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    data["requestTotal"] = this.requestTotal
      ? this.requestTotal.toJSON()
      : <any>undefined;
    data["status"] = this.status;
    return data;
  }
}

export class ValidationResultOfInterviewsRequestDto
  extends ValidationResult
  implements IValidationResultOfInterviewsRequestDto
{
  value?: InterviewsRequestDto | undefined;

  constructor(data?: IValidationResultOfInterviewsRequestDto) {
    super(data);
  }

  init(_data?: any) {
    super.init(_data);
    if (_data) {
      this.value = _data["value"]
        ? InterviewsRequestDto.fromJS(_data["value"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): ValidationResultOfInterviewsRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new ValidationResultOfInterviewsRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["value"] = this.value ? this.value.toJSON() : <any>undefined;
    super.toJSON(data);
    return data;
  }
}

export interface IValidationResultOfInterviewsRequestDto extends IValidationResult {
  value?: InterviewsRequestDto | undefined;
}

export class SealedObject implements ISealedObject {
  constructor(data?: ISealedObject) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {}

  static fromJS(data: any): SealedObject {
    data = typeof data === "object" ? data : {};
    let result = new SealedObject();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    return data;
  }
}

export interface ISealedObject {}

/** Interview request */
export class InterviewsRequestDto
  extends SealedObject
  implements IInterviewsRequestDto
{
  /** If of the entity */
  interviewRequestId: string;
  /** Request participants */
  requestParticipant?: ParticipantDto[] | undefined;
  /** Broadcaster Id */
  broadcaster?: string | undefined;
  /** Company Id */
  companyId?: string | undefined;
  /** Is Cameras Enabled */
  isCamerasEnabled: boolean;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request status */
  status: RequestStatus;
  /** League note */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note */
  broadcasterNote?: NoteDto | undefined;
  /** Id of creator */
  createdBy?: string | undefined;
  /** Date of creation */
  createOnUtc: moment.Moment;
  /** UserModel id who made last update */
  updatedBy?: string | undefined;
  /** Date of last update */
  updatedOnUtc: moment.Moment;
  wasApproved: boolean;
  cameraOperator?: CameraOperator;

  constructor(data?: IInterviewsRequestDto) {
    super(data);
  }

  init(_data?: any) {
    super.init(_data);
    if (_data) {
      this.interviewRequestId = _data["interviewRequestId"];
      if (Array.isArray(_data["requestParticipant"])) {
        this.requestParticipant = [] as any;
        for (let item of _data["requestParticipant"])
          this.requestParticipant.push(ParticipantDto.fromJS(item));
      }
      this.broadcaster = _data["broadcaster"];
      this.companyId = _data["companyId"];
      this.isCamerasEnabled = _data["isCamerasEnabled"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      this.status = _data["status"];
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.broadcasterNote = _data["broadcasterNote"]
        ? NoteDto.fromJS(_data["broadcasterNote"])
        : <any>undefined;
      this.createdBy = _data["createdBy"];
      this.createOnUtc = _data["createOnUtc"]
        ? moment.parseZone(_data["createOnUtc"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedOnUtc = _data["updatedOnUtc"]
        ? moment.parseZone(_data["updatedOnUtc"].toString())
        : <any>undefined;
      this.wasApproved = _data["wasApproved"];
      this.cameraOperator = _data["cameraOperator"];
    }
  }

  static fromJS(data: any): InterviewsRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewsRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["interviewRequestId"] = this.interviewRequestId;
    if (Array.isArray(this.requestParticipant)) {
      data["requestParticipant"] = [];
      for (let item of this.requestParticipant)
        data["requestParticipant"].push(item.toJSON());
    }
    data["broadcaster"] = this.broadcaster;
    data["companyId"] = this.companyId;
    data["isCamerasEnabled"] = this.isCamerasEnabled;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    data["status"] = this.status;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["broadcasterNote"] = this.broadcasterNote
      ? this.broadcasterNote.toJSON()
      : <any>undefined;
    data["createdBy"] = this.createdBy;
    data["createOnUtc"] = this.createOnUtc
      ? this.createOnUtc.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedOnUtc"] = this.updatedOnUtc
      ? this.updatedOnUtc.toISOString(true)
      : <any>undefined;
    data["wasApproved"] = this.wasApproved;
    data["cameraOperator"] = this.cameraOperator;
    super.toJSON(data);
    return data;
  }
}

/** Interview request */
export interface IInterviewsRequestDto extends ISealedObject {
  /** If of the entity */
  interviewRequestId: string;
  /** Request participants */
  requestParticipant?: ParticipantDto[] | undefined;
  /** Broadcaster Id */
  broadcaster?: string | undefined;
  /** Company Id */
  companyId?: string | undefined;
  /** Is Cameras Enabled */
  isCamerasEnabled: boolean;
  /** Flag indicates that broadcaster will send own reporters */
  isReportersEnabled: boolean;
  /** Flag indicates that broadcaster will send own producers */
  isProducersEnabled: boolean;
  /** Flag indicates that broadcaster send some additional persons */
  isOthersEnabled: boolean;
  /** Request status */
  status: RequestStatus;
  /** League note */
  leagueNote?: NoteDto | undefined;
  /** Broadcaster note */
  broadcasterNote?: NoteDto | undefined;
  /** Id of creator */
  createdBy?: string | undefined;
  /** Date of creation */
  createOnUtc: moment.Moment;
  /** UserModel id who made last update */
  updatedBy?: string | undefined;
  /** Date of last update */
  updatedOnUtc: moment.Moment;
  wasApproved: boolean;
}

export class ValidationMessge implements IValidationMessge {
  message?: string | undefined;

  constructor(data?: IValidationMessge) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.message = _data["message"];
    }
  }

  static fromJS(data: any): ValidationMessge {
    data = typeof data === "object" ? data : {};
    let result = new ValidationMessge();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["message"] = this.message;
    return data;
  }
}

export interface IValidationMessge {
  message?: string | undefined;
}

export class CreateInterviewsRequestDto
  extends InterviewsRequestDto
  implements ICreateInterviewsRequestDto
{
  /** Interview id */
  interviewId: string;

  constructor(data?: ICreateInterviewsRequestDto) {
    super(data);
  }

  init(_data?: any) {
    super.init(_data);
    if (_data) {
      this.interviewId = _data["interviewId"];
    }
  }

  static fromJS(data: any): CreateInterviewsRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateInterviewsRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["interviewId"] = this.interviewId;
    super.toJSON(data);
    return data;
  }
}

export interface ICreateInterviewsRequestDto extends IInterviewsRequestDto {
  /** Interview id */
  interviewId: string;
}

export class InterviewTypeDto implements IInterviewTypeDto {
  interviewTypeId: string;
  interviewTypeName?: string | undefined;
  description?: string | undefined;

  constructor(data?: IInterviewTypeDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.interviewTypeId = _data["interviewTypeId"];
      this.interviewTypeName = _data["interviewTypeName"];
      this.description = _data["description"];
    }
  }

  static fromJS(data: any): InterviewTypeDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewTypeDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["interviewTypeId"] = this.interviewTypeId;
    data["interviewTypeName"] = this.interviewTypeName;
    data["description"] = this.description;
    return data;
  }
}

export interface IInterviewTypeDto {
  interviewTypeId: string;
  interviewTypeName?: string | undefined;
  description?: string | undefined;
}

export class InterviewDto implements IInterviewDto {
  /** Event id */
  interviewId: string;
  /** Interview type */
  type?: InterviewTypeDto | undefined;
  /** Arrival date */
  arrival: moment.Moment;
  /** Start date */
  start: moment.Moment;
  /** Interviewee */
  interviewee?: string | undefined;
  /** Location */
  locationId: string;
  /** Location title for broadcaster */
  locationTitle?: string | undefined;
  status: EventStatus;
  requestStatus: RequestStatus;
  totalRequests: number;
  managedRequests: number;
  approvedRequests: number;
  rejectedRequests: number;

  constructor(data?: IInterviewDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.interviewId = _data["interviewId"];
      this.type = _data["type"]
        ? InterviewTypeDto.fromJS(_data["type"])
        : <any>undefined;
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.interviewee = _data["interviewee"];
      this.locationId = _data["locationId"];
      this.locationTitle = _data["locationTitle"];
      this.status = _data["status"];
      this.requestStatus = _data["requestStatus"];
      this.totalRequests = _data["totalRequests"];
      this.managedRequests = _data["managedRequests"];
      this.approvedRequests = _data["approvedRequests"];
      this.rejectedRequests = _data["rejectedRequests"];
    }
  }

  static fromJS(data: any): InterviewDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["interviewId"] = this.interviewId;
    data["type"] = this.type ? this.type.toJSON() : <any>undefined;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["interviewee"] = this.interviewee;
    data["locationId"] = this.locationId;
    data["locationTitle"] = this.locationTitle;
    data["status"] = this.status;
    data["requestStatus"] = this.requestStatus;
    data["totalRequests"] = this.totalRequests;
    data["managedRequests"] = this.managedRequests;
    data["approvedRequests"] = this.approvedRequests;
    data["rejectedRequests"] = this.rejectedRequests;
    return data;
  }
}

export interface IInterviewDto {
  /** Event id */
  interviewId: string;
  /** Interview type */
  type?: InterviewTypeDto | undefined;
  /** Arrival date */
  arrival: moment.Moment;
  /** Start date */
  start: moment.Moment;
  /** Interviewee */
  interviewee?: string | undefined;
  /** Location */
  locationId: string;
  /** Location title for broadcaster */
  locationTitle?: string | undefined;
  status: EventStatus;
  requestStatus: RequestStatus;
  totalRequests: number;
  managedRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
}

export class ResultOfInterviewDetailsDto implements IResultOfInterviewDetailsDto {
  isSuccess: boolean;
  value?: InterviewDetailsDto | undefined;

  constructor(data?: IResultOfInterviewDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.isSuccess = _data["isSuccess"];
      this.value = _data["value"]
        ? InterviewDetailsDto.fromJS(_data["value"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): ResultOfInterviewDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new ResultOfInterviewDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["isSuccess"] = this.isSuccess;
    data["value"] = this.value ? this.value.toJSON() : <any>undefined;
    return data;
  }
}

export interface IResultOfInterviewDetailsDto {
  isSuccess: boolean;
  value?: InterviewDetailsDto | undefined;
}

/** Interview details */
export class InterviewDetailsDto implements IInterviewDetailsDto {
  /** Event id */
  interviewId: string;
  /** Interview type name */
  interviewType?: InterviewTypeDto | undefined;
  /** Arrival date */
  arrival: moment.Moment;
  /** Start date */
  start: moment.Moment;
  /** Interviewee */
  interviewee?: string | undefined;
  /** Location */
  locationId: string;
  /** Location title for broadcaster */
  locationTitle?: string | undefined;
  /** League notes */
  leagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event languages */
  interviewLanguages?: InterviewsLanguageDto[] | undefined;
  /** Event requests */
  requests?: InterviewsRequestDto[] | undefined;
  /** Requests statistic */
  requestsInfo?: InterviewsRequestsInfoDto | undefined;
  /** Created by  */
  createdBy?: string | undefined;
  /** Created on Utc */
  createdOnUtc: moment.Moment;
  /** Updated by */
  updatedBy?: string | undefined;
  /** Updated by utc */
  updatedOnUtc: moment.Moment;
  duration?: moment.Duration | undefined;

  constructor(data?: IInterviewDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.interviewId = _data["interviewId"];
      this.interviewType = _data["interviewType"]
        ? InterviewTypeDto.fromJS(_data["interviewType"])
        : <any>undefined;
      this.arrival = _data["arrival"]
        ? moment.parseZone(_data["arrival"].toString())
        : <any>undefined;
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      this.start = _data["start"]
        ? moment.parseZone(_data["start"].toString())
        : <any>undefined;
      this.interviewee = _data["interviewee"];
      this.locationId = _data["locationId"];
      this.locationTitle = _data["locationTitle"];
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.status = _data["status"];
      if (Array.isArray(_data["interviewLanguages"])) {
        this.interviewLanguages = [] as any;
        for (let item of _data["interviewLanguages"])
          this.interviewLanguages.push(InterviewsLanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["requests"])) {
        this.requests = [] as any;
        for (let item of _data["requests"])
          this.requests.push(InterviewsRequestDto.fromJS(item));
      }
      this.requestsInfo = _data["requestsInfo"]
        ? InterviewsRequestsInfoDto.fromJS(_data["requestsInfo"])
        : <any>undefined;
      this.createdBy = _data["createdBy"];
      this.createdOnUtc = _data["createdOnUtc"]
        ? moment.parseZone(_data["createdOnUtc"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
      this.updatedOnUtc = _data["updatedOnUtc"]
        ? moment.parseZone(_data["updatedOnUtc"].toString())
        : <any>undefined;
    }
  }

  static fromJS(data: any): InterviewDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["interviewId"] = this.interviewId;
    data["interviewType"] = this.interviewType
      ? this.interviewType.toJSON()
      : <any>undefined;
    data["arrival"] = this.arrival
      ? this.arrival.toISOString(true)
      : <any>undefined;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    data["start"] = this.start ? this.start.toISOString(true) : <any>undefined;
    data["interviewee"] = this.interviewee;
    data["locationId"] = this.locationId;
    data["locationTitle"] = this.locationTitle;
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["status"] = this.status;
    if (Array.isArray(this.interviewLanguages)) {
      data["interviewLanguages"] = [];
      for (let item of this.interviewLanguages)
        data["interviewLanguages"].push(item.toJSON());
    }
    if (Array.isArray(this.requests)) {
      data["requests"] = [];
      for (let item of this.requests) data["requests"].push(item.toJSON());
    }
    data["requestsInfo"] = this.requestsInfo
      ? this.requestsInfo.toJSON()
      : <any>undefined;
    data["createdBy"] = this.createdBy;
    data["createdOnUtc"] = this.createdOnUtc
      ? this.createdOnUtc.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    data["updatedOnUtc"] = this.updatedOnUtc
      ? this.updatedOnUtc.toISOString(true)
      : <any>undefined;
    return data;
  }
}

/** Interview details */
export interface IInterviewDetailsDto {
  /** Event id */
  interviewId: string;
  /** Interview type name */
  interviewType?: InterviewTypeDto | undefined;
  /** Arrival date */
  arrival: moment.Moment;
  /** Start date */
  start: moment.Moment;
  /** Interviewee */
  interviewee?: string | undefined;
  /** Location */
  locationId: string;
  /** Location title for broadcaster */
  locationTitle?: string | undefined;
  /** League notes */
  leagueNote?: NoteDto | undefined;
  /** Event status */
  status: EventStatus;
  /** Event languages */
  interviewLanguages?: InterviewsLanguageDto[] | undefined;
  /** Event requests */
  requests?: InterviewsRequestDto[] | undefined;
  /** Requests statistic */
  requestsInfo?: InterviewsRequestsInfoDto | undefined;
  /** Created by  */
  createdBy?: string | undefined;
  /** Created on Utc */
  createdOnUtc: moment.Moment;
  /** Updated by */
  updatedBy?: string | undefined;
  /** Updated by utc */
  updatedOnUtc: moment.Moment;
  duration?: moment.Duration | undefined;
}

export class InterviewsLanguageDto implements IInterviewsLanguageDto {
  languageId: string;

  constructor(data?: IInterviewsLanguageDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.languageId = _data["languageId"];
    }
  }

  static fromJS(data: any): InterviewsLanguageDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewsLanguageDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["languageId"] = this.languageId;
    return data;
  }
}

export interface IInterviewsLanguageDto {
  languageId: string;
}

export class InterviewsRequestsInfoDto implements IInterviewsRequestsInfoDto {
  approved: number;
  rejected: number;
  requested: number;

  constructor(data?: IInterviewsRequestsInfoDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.approved = _data["approved"];
      this.rejected = _data["rejected"];
      this.requested = _data["requested"];
    }
  }

  static fromJS(data: any): InterviewsRequestsInfoDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewsRequestsInfoDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["approved"] = this.approved;
    data["rejected"] = this.rejected;
    data["requested"] = this.requested;
    return data;
  }
}

export interface IInterviewsRequestsInfoDto {
  approved: number;
  rejected: number;
  requested: number;
}

export class ClubContentSessionRequestDto implements IClubContentSessionRequestDto {
  requestId: string;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;

  constructor(data?: IClubContentSessionRequestDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.requestId = _data["requestId"];
      this.cameraOperator = _data["cameraOperator"];
      this.isReportersEnabled = _data["isReportersEnabled"];
      this.isProducersEnabled = _data["isProducersEnabled"];
      this.isOthersEnabled = _data["isOthersEnabled"];
      if (Array.isArray(_data["participants"])) {
        this.participants = [] as any;
        for (let item of _data["participants"])
          this.participants.push(ParticipantDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): ClubContentSessionRequestDto {
    data = typeof data === "object" ? data : {};
    let result = new ClubContentSessionRequestDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["requestId"] = this.requestId;
    data["cameraOperator"] = this.cameraOperator;
    data["isReportersEnabled"] = this.isReportersEnabled;
    data["isProducersEnabled"] = this.isProducersEnabled;
    data["isOthersEnabled"] = this.isOthersEnabled;
    if (Array.isArray(this.participants)) {
      data["participants"] = [];
      for (let item of this.participants)
        data["participants"].push(item.toJSON());
    }
    return data;
  }
}

export interface IClubContentSessionRequestDto {
  requestId: string;
  cameraOperator: CameraOperator;
  isReportersEnabled: boolean;
  isProducersEnabled: boolean;
  isOthersEnabled: boolean;
  participants?: ParticipantDto[] | undefined;
}

export interface IInterviewEventDto {
  eventId: string;
  arrivalUtc: moment.Moment;
  peoples?: PlayerDto[] | undefined;
  location?: LocationDto | undefined;
  requestTotal?: RequestTotal | undefined;
  status?: EventStatus;
}

export class InterviewEventDetailsDto implements IInterviewEventDetailsDto {
  eventId: string;
  startUtc: moment.Moment;
  arrivalUtc: moment.Moment;
  locationId: string;
  location?: LocationDto | undefined;
  matchId: number;
  clubId: number;
  duration?: moment.Duration | undefined;
  interviewType: InterviewEventType;
  players: Players;
  status: EventStatus;
  languages?: LanguageDto[] | undefined;
  peoples?: PlayerDto[] | undefined;
  slots?: InterviewEventRequestSlotDto[] | undefined;
  requests?: RequestDetailsDto[] | undefined;
  leagueNote?: NoteDto | undefined;
  clubNote?: NoteDto | undefined;
  updatedOnUtc: moment.Moment;
  updatedBy?: string | undefined;

  constructor(data?: IInterviewEventDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.eventId = _data["eventId"];
      this.startUtc = _data["startUtc"]
        ? moment.parseZone(_data["startUtc"].toString())
        : <any>undefined;
      this.arrivalUtc = _data["arrivalUtc"]
        ? moment.parseZone(_data["arrivalUtc"].toString())
        : <any>undefined;
      this.locationId = _data["location"]
        ? LocationDto.fromJS(_data["location"]).locationId
        : <any>undefined;
      this.location = _data["location"]
        ? LocationDto.fromJS(_data["location"])
        : <any>undefined;
      this.matchId = _data["matchId"];
      this.clubId = _data["clubId"];
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      this.interviewType = _data["interviewType"];
      this.players = _data["players"];
      this.status = _data["status"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PlayerDto.fromJS(item));
      }
      if (Array.isArray(_data["slots"])) {
        this.slots = [] as any;
        for (let item of _data["slots"])
          this.slots.push(InterviewEventRequestSlotDto.fromJS(item));
      }
      if (Array.isArray(_data["requests"])) {
        this.requests = [] as any;
        for (let item of _data["requests"])
          this.requests.push(RequestDetailsDto.fromJS(item));
      }
      this.leagueNote = _data["leagueNote"]
        ? NoteDto.fromJS(_data["leagueNote"])
        : <any>undefined;
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
      this.updatedOnUtc = _data["updatedOnUtc"]
        ? moment.parseZone(_data["updatedOnUtc"].toString())
        : <any>undefined;
      this.updatedBy = _data["updatedBy"];
    }
  }

  static fromJS(data: any): InterviewEventDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewEventDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["eventId"] = this.eventId;
    data["startUtc"] = this.startUtc
      ? this.startUtc.toISOString(true)
      : <any>undefined;
    data["arrivalUtc"] = this.arrivalUtc
      ? this.arrivalUtc.toISOString(true)
      : <any>undefined;
    data["locationId"] = this.locationId;
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    data["matchId"] = this.matchId;
    data["clubId"] = this.clubId;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    data["interviewType"] = this.interviewType;
    data["players"] = this.players;
    data["status"] = this.status;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    if (Array.isArray(this.slots)) {
      data["slots"] = [];
      for (let item of this.slots) data["slots"].push(item.toJSON());
    }
    if (Array.isArray(this.requests)) {
      data["requests"] = [];
      for (let item of this.requests) data["requests"].push(item.toJSON());
    }
    data["leagueNote"] = this.leagueNote
      ? this.leagueNote.toJSON()
      : <any>undefined;
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["updatedOnUtc"] = this.updatedOnUtc
      ? this.updatedOnUtc.toISOString(true)
      : <any>undefined;
    data["updatedBy"] = this.updatedBy;
    return data;
  }
}

export interface IInterviewEventDetailsDto {
  eventId: string;
  startUtc: moment.Moment;
  arrivalUtc: moment.Moment;
  locationId: string;
  location?: LocationDto | undefined;
  matchId: number;
  clubId: number;
  duration?: moment.Duration | undefined;
  interviewType: InterviewEventType;
  players: Players;
  status: EventStatus;
  languages?: LanguageDto[] | undefined;
  peoples?: PlayerDto[] | undefined;
  slots?: InterviewEventRequestSlotDto[] | undefined;
  requests?: RequestDetailsDto[] | undefined;
  leagueNote?: NoteDto | undefined;
  clubNote?: NoteDto | undefined;
  updatedOnUtc: moment.Moment;
  updatedBy?: string | undefined;
}

export enum InterviewEventType {
  SingleInterview = 0,
  MultiSessions = 1,
}

export enum Players {
  Single = 0,
  Multiple = 1,
}

export class InterviewEventRequestSlotDto implements IInterviewEventRequestSlotDto {
  id: string;
  requestId?: string | undefined;
  startTime: moment.Moment;
  endTime: moment.Moment;
  submittedRequests?: string[] | undefined;
  languages?: LanguageDto[] | undefined;
  peoples?: PlayerDto[] | undefined;

  constructor(data?: IInterviewEventRequestSlotDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.requestId = _data["requestId"];
      this.startTime = _data["startTime"]
        ? moment.parseZone(_data["startTime"].toString())
        : <any>undefined;
      this.endTime = _data["endTime"]
        ? moment.parseZone(_data["endTime"].toString())
        : <any>undefined;
      if (Array.isArray(_data["submittedRequests"])) {
        this.submittedRequests = [] as any;
        for (let item of _data["submittedRequests"])
          this.submittedRequests.push(item);
      }
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PlayerDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): InterviewEventRequestSlotDto {
    data = typeof data === "object" ? data : {};
    let result = new InterviewEventRequestSlotDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["requestId"] = this.requestId;
    data["startTime"] = this.startTime
      ? this.startTime.toISOString(true)
      : <any>undefined;
    data["endTime"] = this.endTime
      ? this.endTime.toISOString(true)
      : <any>undefined;
    if (Array.isArray(this.submittedRequests)) {
      data["submittedRequests"] = [];
      for (let item of this.submittedRequests)
        data["submittedRequests"].push(item);
    }
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    return data;
  }
}

export interface IInterviewEventRequestSlotDto {
  id: string;
  requestId?: string | undefined;
  startTime: moment.Moment;
  endTime: moment.Moment;
  submittedRequests?: string[] | undefined;
  languages?: LanguageDto[] | undefined;
  peoples?: PlayerDto[] | undefined;
}

export class CreateInterviewEventDetailsDto implements ICreateInterviewEventDetailsDto {
  startUtc: moment.Moment;
  arrivalUtc: moment.Moment;
  locationId: string;
  matchId: number;
  duration?: moment.Duration | undefined;
  interviewType: InterviewEventType;
  players: Players;
  status: EventStatus;
  languages?: LanguageDto[] | undefined;
  peoples?: PlayerDto[] | undefined;
  slots?: InterviewEventRequestSlotDto[] | undefined;
  location?: LocationDto;
  clubNote?: NoteDto | undefined;

  constructor(data?: ICreateInterviewEventDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.startUtc = _data["startUtc"]
        ? moment.parseZone(_data["startUtc"].toString())
        : <any>undefined;
      this.arrivalUtc = _data["arrivalUtc"]
        ? moment.parseZone(_data["arrivalUtc"].toString())
        : <any>undefined;
      this.locationId = _data["locationId"];
      this.matchId = _data["matchId"];
      this.duration = _data["duration"]
        ? moment.duration(_data["duration"].toString())
        : <any>undefined;
      this.interviewType = _data["interviewType"];
      this.players = _data["players"];
      this.status = _data["status"];
      if (Array.isArray(_data["languages"])) {
        this.languages = [] as any;
        for (let item of _data["languages"])
          this.languages.push(LanguageDto.fromJS(item));
      }
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PlayerDto.fromJS(item));
      }
      if (Array.isArray(_data["slots"])) {
        this.slots = [] as any;
        for (let item of _data["slots"])
          this.slots.push(InterviewEventRequestSlotDto.fromJS(item));
      }
      this.clubNote = _data["clubNote"]
        ? NoteDto.fromJS(_data["clubNote"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): CreateInterviewEventDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateInterviewEventDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["startUtc"] = this.startUtc
      ? this.startUtc.toISOString(true)
      : <any>undefined;
    data["arrivalUtc"] = this.arrivalUtc
      ? this.arrivalUtc.toISOString(true)
      : <any>undefined;
    data["locationId"] = this.locationId;
    data["matchId"] = this.matchId;
    data["duration"] = this.duration
      ? `${this.duration.hours()}:${this.duration.minutes()}`
      : <any>undefined;
    data["interviewType"] = this.interviewType;
    data["players"] = this.players;
    data["status"] = this.status;
    if (Array.isArray(this.languages)) {
      data["languages"] = [];
      for (let item of this.languages) data["languages"].push(item.toJSON());
    }
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    if (Array.isArray(this.slots)) {
      data["slots"] = [];
      for (let item of this.slots) data["slots"].push(item.toJSON());
    }
    data["clubNote"] = this.clubNote ? this.clubNote.toJSON() : <any>undefined;
    data["location"] = this.location ? this.location.toJSON() : <any>undefined;
    return data;
  }
}

export interface ICreateInterviewEventDetailsDto {
  startUtc: moment.Moment;
  arrivalUtc: moment.Moment;
  locationId: string;
  matchId: number;
  duration?: moment.Duration | undefined;
  interviewType: InterviewEventType;
  players: Players;
  status: EventStatus;
  languages?: LanguageDto[] | undefined;
  peoples?: PlayerDto[] | undefined;
  slots?: InterviewEventRequestSlotDto[] | undefined;
  location?: LocationDto;
  clubNote?: NoteDto | undefined;
}

export class MatchInterviewEventDetailsDto implements IMatchInterviewEventDetailsDto {
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  homeEvent?: InterviewEventDetailsDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  awayEvent?: InterviewEventDetailsDto | undefined;
  matchId: number;
  matchDate: moment.Moment;

  constructor(data?: IMatchInterviewEventDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.homeEvent = _data["homeEvent"]
        ? InterviewEventDetailsDto.fromJS(_data["homeEvent"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
      this.awayEvent = _data["awayEvent"]
        ? InterviewEventDetailsDto.fromJS(_data["awayEvent"])
        : <any>undefined;
      this.matchId = _data["matchId"];
      this.matchDate = _data["matchDate"]
        ? moment.parseZone(_data["matchDate"].toString())
        : <any>undefined;
    }
  }

  static fromJS(data: any): MatchInterviewEventDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new MatchInterviewEventDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["homeEvent"] = this.homeEvent
      ? this.homeEvent.toJSON()
      : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    data["awayEvent"] = this.awayEvent
      ? this.awayEvent.toJSON()
      : <any>undefined;
    data["matchId"] = this.matchId;
    data["matchDate"] = this.matchDate
      ? this.matchDate.toISOString(true)
      : <any>undefined;
    return data;
  }
}

export interface IMatchInterviewEventDetailsDto {
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  homeEvent?: InterviewEventDetailsDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  awayEvent?: InterviewEventDetailsDto | undefined;
  matchId: number;
  matchDate: moment.Moment;
}

export class ClubMatchInterviewEventDetailsDto implements IClubMatchInterviewEventDetailsDto {
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  event?: InterviewEventDetailsDto | undefined;
  matchDate: moment.Moment;
  /** Away team info */
  awayTeam?: TeamDto | undefined;

  constructor(data?: IClubMatchInterviewEventDetailsDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.event = _data["event"]
        ? InterviewEventDetailsDto.fromJS(_data["event"])
        : <any>undefined;
      this.matchDate = _data["matchDate"]
        ? moment.parseZone(_data["matchDate"].toString())
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
    }
  }

  static fromJS(data: any): ClubMatchInterviewEventDetailsDto {
    data = typeof data === "object" ? data : {};
    let result = new ClubMatchInterviewEventDetailsDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["event"] = this.event ? this.event.toJSON() : <any>undefined;
    data["matchDate"] = this.matchDate
      ? this.matchDate.toISOString(true)
      : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    return data;
  }
}

export interface IClubMatchInterviewEventDetailsDto {
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  event?: InterviewEventDetailsDto | undefined;
  matchDate: moment.Moment;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
}

export class ClubMatchInterviewEventDto implements IClubMatchInterviewEventDto {
  matchId: number;
  matchRound: number;
  fixtureDateUtc: moment.Moment;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  status?: EventStatus | undefined;
  type?: InterviewEventType | undefined;
  peoples?: PlayerDto[] | undefined;

  constructor(data?: IClubMatchInterviewEventDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.matchId = _data["matchId"];
      this.matchRound = _data["matchRound"];
      this.fixtureDateUtc = _data["fixtureDateUtc"]
        ? moment.parseZone(_data["fixtureDateUtc"].toString())
        : <any>undefined;
      this.homeTeam = _data["homeTeam"]
        ? TeamDto.fromJS(_data["homeTeam"])
        : <any>undefined;
      this.awayTeam = _data["awayTeam"]
        ? TeamDto.fromJS(_data["awayTeam"])
        : <any>undefined;
      this.status = _data["status"];
      this.type = _data["type"];
      if (Array.isArray(_data["peoples"])) {
        this.peoples = [] as any;
        for (let item of _data["peoples"])
          this.peoples.push(PlayerDto.fromJS(item));
      }
    }
  }

  static fromJS(data: any): ClubMatchInterviewEventDto {
    data = typeof data === "object" ? data : {};
    let result = new ClubMatchInterviewEventDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["matchId"] = this.matchId;
    data["matchRound"] = this.matchRound;
    data["fixtureDateUtc"] = this.fixtureDateUtc
      ? this.fixtureDateUtc.toISOString(true)
      : <any>undefined;
    data["homeTeam"] = this.homeTeam ? this.homeTeam.toJSON() : <any>undefined;
    data["awayTeam"] = this.awayTeam ? this.awayTeam.toJSON() : <any>undefined;
    data["status"] = this.status;
    data["type"] = this.type;
    if (Array.isArray(this.peoples)) {
      data["peoples"] = [];
      for (let item of this.peoples) data["peoples"].push(item.toJSON());
    }
    return data;
  }
}

export interface IClubMatchInterviewEventDto {
  matchId: number;
  matchRound: number;
  fixtureDateUtc: moment.Moment;
  /** Home team info */
  homeTeam?: TeamDto | undefined;
  /** Away team info */
  awayTeam?: TeamDto | undefined;
  status?: EventStatus | undefined;
  type?: InterviewEventType | undefined;
  peoples?: PlayerDto[] | undefined;
}

export class RequestToSlotDto implements IRequestToSlotDto {
  slotId: string;

  constructor(data?: IRequestToSlotDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.slotId = _data["slotId"];
    }
  }

  static fromJS(data: any): RequestToSlotDto {
    data = typeof data === "object" ? data : {};
    let result = new RequestToSlotDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["slotId"] = this.slotId;
    return data;
  }
}

export interface IRequestToSlotDto {
  slotId: string;
}

export class ApproveRequestToSlotDto implements IApproveRequestToSlotDto {
  slotId: string;
  requestId: string;

  constructor(data?: IApproveRequestToSlotDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.slotId = _data["slotId"];
      this.requestId = _data["requestId"];
    }
  }

  static fromJS(data: any): ApproveRequestToSlotDto {
    data = typeof data === "object" ? data : {};
    let result = new ApproveRequestToSlotDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["slotId"] = this.slotId;
    data["requestId"] = this.requestId;
    return data;
  }
}

export interface IApproveRequestToSlotDto {
  slotId: string;
  requestId: string;
}

export interface FileResponse {
  data: Blob;
  status: number;
  fileName?: string;
  headers?: { [name: string]: any };
}

export class SwaggerException extends Error {
  message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any,
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isSwaggerException = true;

  static isSwaggerException(obj: any): obj is SwaggerException {
    return obj.isSwaggerException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any,
): Observable<any> {
  if (result !== null && result !== undefined) return throwError(() => result);
  else
    return throwError(
      new SwaggerException(message, status, response, headers, null),
    );
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((<any>event.target).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}
